/**
 * AlphaX Tuning Simulator | Copyright (C) 2026 AlphaX
 * Licensed under GPL-3.0 (https://www.gnu.org/licenses/gpl-3.0.txt)
 * Subscriptions do not negate this license; derivative works must be open source.
 */
/**
 * AlphaX Tuning Simulator - UI Management & Interaction
 */

(function() {
    const _a = [
        'alphaxpr.com', 
        'tunersim.com', 
        'tuningsim.com', 
        'tunersimulator.com',
        'tuning-sim.firebaseapp.com', 
        'tuning-sim.web.app', 
        'localhost', 
        '127.0.0.1'
    ];
    const _h = window.location.hostname.toLowerCase();
    if (_h !== '' && !_a.some(d => _h === d || _h.endsWith('.' + d))) {
        window.onload = function() { document.body.innerHTML = ''; };
        throw new Error();
    }
})();

let appMode = 'garage'; // 'garage', 'dyno', 'drag'
let advancedMode = false; // when false, advanced ECU features are hidden and bypassed in physics
let twinTurboEnabled = false;
let _currentHasTurbo = false; // tracks whether a turbocharger mod is installed
let activeTableId = null;
let selStart = null;
let selEnd = null;
let isDragging = false;
let axisEditContext = null; // { type: 'col'|'row', idx, tableId } when editing an axis header cell
let pendingMathOp = null; // '+' | '-' | '*' when a math shortcut key was pressed

// --- UNDO / REDO ---
const undoStack = [];
const redoStack = [];
const MAX_UNDO = 10;

// Trigger calibration animation state
let crankAnimFrame = null;
let lastCrankTimestamp = null;
let cumulativeAngle = 0;
let _trigCalPrevCorrect = false;

// --- BINARY SAVE ---
let ECU_MEMORY_MAP = null;
let _pendingBinaryAction = null; // callback to run once the map is loaded

function ensureMemoryMap(onReady) {
    if (ECU_MEMORY_MAP) { onReady(); return; }
    _pendingBinaryAction = onReady;
    document.getElementById('file-memmap-upload').click();
}

// Maps memory-map field names (JSON export keys, snake_case) → ECU object property names (camelCase).
// Settings scalars share the same name in both and need no entry here.
const ECU_FIELD_MAP = {
    // 2D tables
    ve:                    'veTable',
    target_afr:            'targetAfrTable',
    ignition:              'timingTable',
    vvt_intake:            'vvtIntakeTable',
    vvt_exhaust:           'vvtExhaustTable',
    wg_duty:               'wgDutyTable',
    flex_ve:               'flexVeTable',
    flex_afr:              'flexAfrTable',
    flex_timing:           'flexTimingTable',
    flex_boost:            'flexBoostTable',
    accel_ms_table:        'accelMsTable',
    accel_map_table:       'accelMapTable',
    alt_volt_table:        'altVoltTable',
    cl_boost_target:       'clBoostTargetTable',
    // 1D curves
    flex_ve_curve:         'flexVeBlendCurve',
    flex_afr_curve:        'flexAfrBlendCurve',
    flex_timing_curve:     'flexTimingBlendCurve',
    flex_boost_curve:      'flexBoostBlendCurve',
    idle_duty_curve:       'idleDutyCurve',
    ect_fuel_curve:        'ectFuelCurve',
    iat_timing_curve:      'iatTimingCurve',
    iat_ve_curve:          'iatVeCurve',
    knock_threshold_curve: 'knockThresholdCurve',
    inj_dt_curve:          'injDtCurve',
    coil_dwell_curve:      'coilDwellCurve',
    // Axes
    rpm_axis:              'rpmAxis',
    map_axis:              'mapAxis',
    vvt_rpm_axis:          'vvtRpmAxis',
    vvt_map_axis:          'vvtMapAxis',
    wg_rpm_axis:           'wgRpmAxis',
    tps_axis:              'tpsAxis',
    flex_rpm_axis:         'flexRpmAxis',
    flex_ethanol_axis:     'flexEthanolAxis',
    flex_map_axis:         'flexMapAxis',
    flex_tps_axis:         'flexTpsAxis',
    idle_duty_coolant_axis:'idleDutyCoolantAxis',
    ect_fuel_axis:         'ectFuelAxis',
    iat_timing_axis:       'iatTimingAxis',
    iat_ve_axis:           'iatVeAxis',
    knock_threshold_axis:  'knockThresholdAxis',
    accel_ms_dtps_axis:    'accelMsDtpsAxis',
    accel_ms_rpm_axis:     'accelMsRpmAxis',
    accel_map_tps_axis:    'accelMapTpsAxis',
    accel_map_rpm_axis:    'accelMapRpmAxis',
    inj_dt_axis:           'injDtAxis',
    coil_dwell_axis:          'coilDwellAxis',
    cranking_idle_duty_axis:  'crankingIdleDutyAxis',
    cranking_idle_duty_curve: 'crankingIdleDutyCurve',
    post_crank_enrich_axis:   'postCrankEnrichAxis',
    post_crank_enrich_curve:  'postCrankEnrichCurve',
    idle_target_rpm_axis:     'idleTargetRpmAxis',
    idle_target_rpm_curve:    'idleTargetRpmCurve',
    alt_volt_rpm_axis:     'altVoltRpmAxis',
    alt_volt_load_axis:    'altVoltLoadAxis',
    cl_boost_rpm_axis:     'clBoostRpmAxis',
    cl_boost_tps_axis:     'clBoostTpsAxis',
};

const ECU_ENUM_ENCODE = {
    ignitionMode:          { "distributor": 1, "wasted_spark": 2, "sequential": 3, "sequential_cop": 4 },
    accelMode:             { "none": 0, "ms_adder": 1, "map_predict": 2 },
    lcCutType:             { "fuel": 1, "spark": 2, "both": 3 },
    nitrousShotType:       { "dry": 1, "wet": 2 },
    nitrousActivationMode: { "switch": 1, "tps": 2 },
    fuelSystemType:        { "returnless": 1, "return": 2 }
};
const ECU_ENUM_DECODE = {};
for (const [field, map] of Object.entries(ECU_ENUM_ENCODE)) {
    ECU_ENUM_DECODE[field] = Object.fromEntries(Object.entries(map).map(([k, v]) => [v, k]));
}

async function tryFetchMemoryMap() {
    try {
        const url = window.ECU_MEMORY_MAP_URL || './tools/ecu_memory_map.json';
        const res = await fetch(url);
        if (res.ok) { const d = await res.json(); if (Array.isArray(d)) ECU_MEMORY_MAP = d; }
    } catch (e) { /* silent — user loads manually under file:// */ }
}
document.addEventListener('DOMContentLoaded', tryFetchMemoryMap);


function switchAppMode(mode) {
    const DRAG_LOCKED_PHASES = new Set(['staging1', 'staging2', 'staging3', 'staging4', 'waitForLaunch', 'pull']);
    if (mode !== 'drag' && typeof dragPhase !== 'undefined' && DRAG_LOCKED_PHASES.has(dragPhase)) {
        showModToast('Cannot leave the drag strip once staged!', true);
        const selector = document.getElementById('app-mode-selector');
        if (selector) selector.value = 'drag';
        return;
    }
    if (mode !== 'dyno' && typeof isDynoPullActive !== 'undefined' && isDynoPullActive && typeof dynoPhase !== 'undefined' && dynoPhase === 'pull') {
        showModToast('Cannot leave the dyno during an active pull!', true);
        const selector = document.getElementById('app-mode-selector');
        if (selector) selector.value = 'dyno';
        return;
    }
    appMode = mode;
    if (mode !== 'drag') {
        const submitWrap = document.getElementById('leaderboard-submit-wrap');
        if (submitWrap) submitWrap.classList.add('hidden');
    }
    document.getElementById('view-garage').classList.toggle('hidden', mode !== 'garage');
    document.getElementById('view-dyno').classList.toggle('hidden', mode !== 'dyno');
    document.getElementById('view-drag').classList.toggle('hidden', mode !== 'drag');
    const lbView = document.getElementById('view-leaderboards');
    if (lbView) lbView.classList.toggle('hidden', mode !== 'leaderboards');

    // Sync the select dropdown
    const selector = document.getElementById('app-mode-selector');
    if (selector) selector.value = mode;

    if (mode === 'dyno') {
        if (typeof dynoChartInstance !== 'undefined' && dynoChartInstance) dynoChartInstance.resize();
        if (typeof afrChartInstance !== 'undefined' && afrChartInstance) afrChartInstance.resize();
        if (typeof boostChartInstance !== 'undefined' && boostChartInstance) boostChartInstance.resize();
    } else if (mode === 'drag') {
        if (typeof updateCategoryTicker === 'function') updateCategoryTicker();
    } else if (mode === 'leaderboards') {
        if (typeof fetchLeaderboards === 'function') fetchLeaderboards();
    }

    updateQuickNav();
}

/**
 * Updates the two quick nav buttons in the header based on current appMode.
 */
function updateQuickNav() {
    const btn1 = document.getElementById('nav-btn-1');
    const btn2 = document.getElementById('nav-btn-2');
    if (!btn1 || !btn2) return;

    // SVG Icons
    const ICON_GARAGE = `<svg class="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>`; // Car icon (no box)
    const ICON_DYNO = `<svg class="w-5 h-5 fill-current" viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7zm4-3h2v10h-2zm4 6h2v4h-2z"/></svg>`; // Dyno sheet (bars)
    const ICON_DRAG = `<svg class="w-5 h-5 fill-current" viewBox="0 0 24 24"><rect x="11" y="2" width="2" height="20" rx="1"/><circle cx="7" cy="5" r="2"/><circle cx="17" cy="5" r="2"/><circle cx="7" cy="10" r="2"/><circle cx="17" cy="10" r="2"/><circle cx="7" cy="15" r="2"/><circle cx="17" cy="15" r="2"/><circle cx="7" cy="20" r="2"/><circle cx="17" cy="20" r="2"/></svg>`; // Drag Christmas Tree (Two columns of lights)

    if (appMode === 'garage') {
        // Show Dyno and Drag
        btn1.innerHTML = ICON_DYNO;
        btn1.title = "Go to Dyno";
        btn1.onclick = () => switchAppMode('dyno');

        btn2.innerHTML = ICON_DRAG;
        btn2.title = "Go to Drag Strip";
        btn2.onclick = () => switchAppMode('drag');
    } else if (appMode === 'dyno') {
        // Show Garage and Drag
        btn1.innerHTML = ICON_GARAGE;
        btn1.title = "Return to Garage";
        btn1.onclick = () => switchAppMode('garage');

        btn2.innerHTML = ICON_DRAG;
        btn2.title = "Go to Drag Strip";
        btn2.onclick = () => switchAppMode('drag');
    } else if (appMode === 'drag') {
        // Show Garage and Dyno
        btn1.innerHTML = ICON_GARAGE;
        btn1.title = "Return to Garage";
        btn1.onclick = () => switchAppMode('garage');

        btn2.innerHTML = ICON_DYNO;
        btn2.title = "Go to Dyno";
        btn2.onclick = () => switchAppMode('dyno');
    }
}

// Ensure quick nav is updated on init; auto-show welcome for zero-purchase users
document.addEventListener('DOMContentLoaded', () => {
    updateQuickNav();

    if (!localStorage.getItem('alphax_welcome_dismissed')) {
        const hasPurchases = window.shopifyCustomer
            && window.shopifyCustomer.loggedIn
            && ((window.hostedEngines || []).length > 0
                || window.hasAdvancedMode
                || (window.hostedMods || []).length > 0);
        if (!hasPurchases) showWelcomeModal();
    }
});

function showWelcomeModal() {
    const modal = document.getElementById('welcome-modal');
    if (modal) {
        modal.classList.remove('hidden');
        helpModalPage(1);
    }
}

function helpModalPage(page) {
    const p1 = document.getElementById('help-page-1');
    const p2 = document.getElementById('help-page-2');
    const btnPrev = document.getElementById('help-btn-prev');
    const btnNext = document.getElementById('help-btn-next');
    const btnDone = document.getElementById('help-btn-done');
    const indicator = document.getElementById('help-page-indicator');
    const neverLabel = document.getElementById('help-never-show-label');
    if (!p1 || !p2) return;
    if (page === 1) {
        p1.classList.remove('hidden');
        p2.classList.add('hidden');
        btnPrev.classList.add('hidden');
        btnNext.classList.remove('hidden');
        btnDone.classList.add('hidden');
        if (neverLabel) neverLabel.classList.add('hidden');
        if (indicator) indicator.textContent = '1 / 2';
    } else {
        p1.classList.add('hidden');
        p2.classList.remove('hidden');
        btnPrev.classList.remove('hidden');
        btnNext.classList.add('hidden');
        btnDone.classList.remove('hidden');
        if (neverLabel) neverLabel.classList.remove('hidden');
        if (indicator) indicator.textContent = '2 / 2';
    }
}

function hideWelcomeModal() {
    const modal = document.getElementById('welcome-modal');
    if (!modal) return;
    const neverShow = document.getElementById('welcome-never-show');
    if (neverShow && neverShow.checked) {
        localStorage.setItem('alphax_welcome_dismissed', '1');
    }
    modal.classList.add('hidden');
}


function switchTab(tabId, dropdownBtnElement) {
    if ((tabId === 'tab-knock-control' || tabId === 'tab-flex-fuel') && !advancedMode) return;
    document.querySelectorAll('.tab-content').forEach(c => c.classList.remove('active'));
    document.querySelectorAll('.tab-dropdown-btn').forEach(b => {
        b.classList.remove('text-blue-400', 'border-blue-500');
        b.classList.add('text-gray-400', 'border-transparent');
    });

    const targetTab = document.getElementById(tabId);
    if (targetTab) targetTab.classList.add('active');
    
    if (dropdownBtnElement) {
        dropdownBtnElement.classList.remove('text-gray-400', 'border-transparent');
        dropdownBtnElement.classList.add('text-blue-400', 'border-blue-500');
    }
    
    activeTableId = null;
    renderSelection();
    hideInlineEditor();

    if (tabId === 'tab-knock-control' && typeof spectrogramChartInstance !== 'undefined' && spectrogramChartInstance) {
        spectrogramChartInstance.resize();
    }
}

// --- TABLE GENERATION ---

function generateHTMLTable(containerId, tableArray, x_axis, y_axis, tableId = '', yAxisLabel = '') {
    // Calculate min/max for heatmap scaling
    let min = Infinity, max = -Infinity;
    for (let r = 0; r < tableArray.length; r++) {
        for (let c = 0; c < tableArray[r].length; c++) {
            const val = tableArray[r][c];
            if (val < min) min = val;
            if (val > max) max = val;
        }
    }
    if (max === min) max = min + 1;

    let html = `<table class="tune-table"><thead><tr><th class="axis-header text-[9px] text-gray-600 font-bold tracking-widest bg-transparent">${yAxisLabel}</th>`;
    x_axis.forEach((val, idx) => html += `<th class="axis-header" data-axis="col" data-idx="${idx}">${val}</th>`);
    html += '</tr></thead><tbody>';
    for(let i = y_axis.length - 1; i >= 0; i--) {
        html += `<tr><th class="axis-header w-12" data-axis="row" data-idx="${i}">${y_axis[i]}</th>`;
        for(let j = 0; j < x_axis.length; j++) {
            const val = tableArray[i][j];
            const pct = (val - min) / (max - min);
            // AFR uses Blue->Red (low->high), others use Green->Red (high->low)
            const hue = (tableId === 'afr' || tableId === 'flex-afr') ? (pct * 240) : ((1 - pct) * 240);
            html += `<td data-r="${i}" data-c="${j}" style="--heatmap-bg: hsl(${hue}, 65%, 25%)">${Math.round(val * 10) / 10}</td>`;
        }
        html += '</tr>';
    }
    html += '</tbody></table>';
    const container = document.getElementById(containerId);
    if (container) container.innerHTML = html;
}

function getTableDecimals(tableId) {
    return (tableId === 'inj-dt-curve' || tableId === 'ect-fuel-curve' || tableId === 'post-crank-enrich-curve') ? 2 : 1;
}

function generate1DHTMLTable(containerId, dataArray, x_axis, unit = '%', tableId = '', decimals = 1) {
    // Calculate min/max for heatmap scaling
    let min = Math.min(...dataArray);
    let max = Math.max(...dataArray);
    if (max === min) max = min + 1;

    let html = '<table class="tune-table"><thead><tr>';
    x_axis.forEach(val => html += `<th class="axis-header">${val}${unit}</th>`);
    html += '</tr></thead><tbody><tr>';
    for(let j = 0; j < x_axis.length; j++) {
        const val = dataArray[j];
        const pct = (val - min) / (max - min);
        const hue = (tableId === 'afr' || tableId === 'flex-afr') ? (pct * 240) : ((1 - pct) * 240);
        html += `<td data-r="0" data-c="${j}" style="--heatmap-bg: hsl(${hue}, 65%, 25%)">${decimals > 1 ? val.toFixed(decimals) : Math.round(val * 10) / 10}</td>`;
    }
    html += '</tr></tbody></table>';
    const container = document.getElementById(containerId);
    if (container) container.innerHTML = html;
}

function refreshHeatmap(tableId) {
    let tableData;
    if (tableId === 've') tableData = ecu.veTable;
    else if (tableId === 'afr') tableData = ecu.targetAfrTable;
    else if (tableId === 'timing') tableData = ecu.timingTable;
    else if (tableId === 'vvt-intake') tableData = ecu.vvtIntakeTable;
    else if (tableId === 'vvt-exhaust') tableData = ecu.vvtExhaustTable;
    else if (tableId === 'wg-duty') tableData = ecu.wgDutyTable;
    else if (tableId === 'alt-volt') tableData = ecu.altVoltTable;
    else if (tableId === 'flex-ve') tableData = ecu.flexVeTable;
    else if (tableId === 'flex-afr') tableData = ecu.flexAfrTable;
    else if (tableId === 'flex-timing') tableData = ecu.flexTimingTable;
    else if (tableId === 'flex-boost') tableData = ecu.flexBoostTable;
    else if (tableId === 'flex-ve-curve') tableData = [ecu.flexVeBlendCurve];
    else if (tableId === 'flex-afr-curve') tableData = [ecu.flexAfrBlendCurve];
    else if (tableId === 'flex-timing-curve') tableData = [ecu.flexTimingBlendCurve];
    else if (tableId === 'flex-boost-curve') tableData = [ecu.flexBoostBlendCurve];
    else if (tableId === 'idle-rpm-curve')    tableData = [ecu.idleTargetRpmCurve];
    else if (tableId === 'idle-duty-curve')   tableData = [ecu.idleDutyCurve];
    else if (tableId === 'ect-fuel-curve')    tableData = [ecu.ectFuelCurve];
    else if (tableId === 'cranking-duty-curve') tableData = [ecu.crankingIdleDutyCurve];
    else if (tableId === 'post-crank-enrich-curve') tableData = [ecu.postCrankEnrichCurve];
    else if (tableId === 'iat-ve-curve')      tableData = [ecu.iatVeCurve];
    else if (tableId === 'inj-dt-curve')      tableData = [ecu.injDtCurve];
    else if (tableId === 'coil-dwell-curve')  tableData = [ecu.coilDwellCurve];
    else if (tableId === 'iat-timing-curve')  tableData = [ecu.iatTimingCurve];
    else if (tableId === 'knock-threshold-curve') tableData = [ecu.knockThresholdCurve];
    else if (tableId === 'accel-ms')          tableData = ecu.accelMsTable;
    else if (tableId === 'accel-map')         tableData = ecu.accelMapTable;
    else if (tableId === 'cl-boost-target')   tableData = ecu.clBoostTargetTable;
    else if (tableId === 'boost-duty-gear-comp-curve') tableData = [ecu.boostDutyGearComp];
    else if (tableId === 'boost-target-gear-comp-curve') tableData = [ecu.boostTargetGearComp];

    const suffix = tableId.endsWith('-curve') ? '' : '-table';
    const tableContainer = document.getElementById(`container-${tableId}${suffix}`);
    if (!tableContainer || !tableData) return;

    let min = Infinity, max = -Infinity;
    for (let r = 0; r < tableData.length; r++) {
        for (let c = 0; c < tableData[r].length; c++) {
            if (tableData[r][c] < min) min = tableData[r][c];
            if (tableData[r][c] > max) max = tableData[r][c];
        }
    }
    if (max === min) max = min + 1;

    const cellMap = new Map();
    tableContainer.querySelectorAll('td[data-r][data-c]').forEach(td => {
        cellMap.set(`${td.dataset.r},${td.dataset.c}`, td);
    });

    const isAfrTable = tableId === 'afr' || tableId === 'flex-afr';
    for (let r = 0; r < tableData.length; r++) {
        for (let c = 0; c < tableData[r].length; c++) {
            const val = tableData[r][c];
            const pct = (val - min) / (max - min);
            const hue = isAfrTable ? (pct * 240) : ((1 - pct) * 240);
            const td = cellMap.get(`${r},${c}`);
            if (td) td.style.setProperty('--heatmap-bg', `hsl(${hue}, 65%, 25%)`);
        }
    }
}

function renderAllTables() {
    if (!ecu) return;
    const isImperial = typeof useMetric !== 'undefined' && !useMetric;
    const isGauge = typeof mapDisplayGauge !== 'undefined' && mapDisplayGauge;
    const baroForDisplay = isGauge ? (parseFloat(document.getElementById('sim-baro')?.value) || 101.3) : 0;
    const tUnit = isImperial ? '°F' : '°C';
    const tAxis = arr => isImperial ? arr.map(c => Math.round(c * 9 / 5 + 32)) : arr;
    const pAxis = arr => {
        const adjusted = arr.map(kpa => kpa - baroForDisplay);
        return isImperial ? adjusted.map(v => parseFloat((v * 0.14504).toFixed(1))) : adjusted.map(v => parseFloat(v.toFixed(1)));
    };
    const mapLabel = isGauge ? 'MAPg' : 'MAP';

    generateHTMLTable('container-ve-table', ecu.veTable, ecu.rpmAxis, pAxis(ecu.mapAxis), 've', mapLabel);
    generateHTMLTable('container-afr-table', ecu.targetAfrTable, ecu.rpmAxis, pAxis(ecu.mapAxis), 'afr', mapLabel);
    generateHTMLTable('container-timing-table', ecu.timingTable, ecu.rpmAxis, pAxis(ecu.mapAxis), 'timing', mapLabel);

    generateHTMLTable('container-vvt-intake-table', ecu.vvtIntakeTable, ecu.vvtRpmAxis, pAxis(ecu.vvtMapAxis), 'vvt-intake', mapLabel);
    generateHTMLTable('container-vvt-exhaust-table', ecu.vvtExhaustTable, ecu.vvtRpmAxis, pAxis(ecu.vvtMapAxis), 'vvt-exhaust', mapLabel);

    generateHTMLTable('container-wg-duty-table', ecu.wgDutyTable, ecu.wgRpmAxis, ecu.tpsAxis, 'wg-duty', 'TPS%');
    generateHTMLTable('container-cl-boost-target-table', ecu.clBoostTargetTable, ecu.clBoostRpmAxis, ecu.clBoostTpsAxis, 'cl-boost-target', 'TPS%');

    // Idle Curves
    generate1DHTMLTable('container-idle-rpm-curve',  ecu.idleTargetRpmCurve, tAxis(ecu.idleTargetRpmAxis),   tUnit, 'idle-rpm-curve');
    generate1DHTMLTable('container-idle-duty-curve', ecu.idleDutyCurve,      tAxis(ecu.idleDutyCoolantAxis), tUnit, 'idle-duty-curve');

    // ECT Fuel Enrichment Curve
    generate1DHTMLTable('container-ect-fuel-curve', ecu.ectFuelCurve, tAxis(ecu.ectFuelAxis), tUnit, 'ect-fuel-curve', 2);

    // Cranking Idle Air Duty vs Coolant Temp
    generate1DHTMLTable('container-cranking-duty-curve', ecu.crankingIdleDutyCurve, tAxis(ecu.crankingIdleDutyAxis), tUnit, 'cranking-duty-curve');

    // Post-Crank Enrichment vs Coolant Temp
    generate1DHTMLTable('container-post-crank-enrich-curve', ecu.postCrankEnrichCurve, tAxis(ecu.postCrankEnrichAxis), tUnit, 'post-crank-enrich-curve', 2);

    // IAT VE Compensation Curve
    generate1DHTMLTable('container-iat-ve-curve', ecu.iatVeCurve, tAxis(ecu.iatVeAxis), tUnit, 'iat-ve-curve');

    // Voltage-dependent curves
    generate1DHTMLTable('container-inj-dt-curve',     ecu.injDtCurve,     ecu.injDtAxis,     'V', 'inj-dt-curve', 2);
    generate1DHTMLTable('container-coil-dwell-curve', ecu.coilDwellCurve, ecu.coilDwellAxis, 'V', 'coil-dwell-curve');

    // IAT Timing Retard Curve
    generate1DHTMLTable('container-iat-timing-curve', ecu.iatTimingCurve, tAxis(ecu.iatTimingAxis), tUnit, 'iat-timing-curve');

    // Knock Threshold Curve
    generate1DHTMLTable('container-knock-threshold-curve', ecu.knockThresholdCurve, ecu.knockThresholdAxis, ' RPM', 'knock-threshold-curve');

    // Flex Blend Curves
    generate1DHTMLTable('container-flex-ve-curve', ecu.flexVeBlendCurve, ecu.ethXAxis, '%', 'flex-ve-curve');
    generate1DHTMLTable('container-flex-afr-curve', ecu.flexAfrBlendCurve, ecu.ethXAxis, '%', 'flex-afr-curve');
    generate1DHTMLTable('container-flex-timing-curve', ecu.flexTimingBlendCurve, ecu.ethXAxis, '%', 'flex-timing-curve');
    generate1DHTMLTable('container-flex-boost-curve', ecu.flexBoostBlendCurve, ecu.ethXAxis, '%', 'flex-boost-curve');

    // Smart Alternator Target Voltage Table
    generateHTMLTable('container-alt-volt-table', ecu.altVoltTable, ecu.altVoltRpmAxis, pAxis(ecu.altVoltLoadAxis), 'alt-volt', mapLabel);

    // Flex 2D Tables
    generateHTMLTable('container-flex-ve-table', ecu.flexVeTable, ecu.flexRpmAxis, pAxis(ecu.flexMapAxis), 'flex-ve', mapLabel);
    generateHTMLTable('container-flex-afr-table', ecu.flexAfrTable, ecu.flexRpmAxis, pAxis(ecu.flexMapAxis), 'flex-afr', mapLabel);
    generateHTMLTable('container-flex-timing-table', ecu.flexTimingTable, ecu.flexRpmAxis, pAxis(ecu.flexMapAxis), 'flex-timing', mapLabel);
    generateHTMLTable('container-flex-boost-table', ecu.flexBoostTable, ecu.flexRpmAxis, ecu.flexTpsAxis, 'flex-boost', 'TPS%');

    generateHTMLTable('container-accel-ms-table',  ecu.accelMsTable,  ecu.accelMsRpmAxis,  ecu.accelMsDtpsAxis, 'accel-ms', 'ΔTPS');
    generateHTMLTable('container-accel-map-table', ecu.accelMapTable, ecu.accelMapRpmAxis, ecu.accelMapTpsAxis, 'accel-map', 'TPS%');
    generate1DHTMLTable('container-boost-duty-gear-comp-curve', ecu.boostDutyGearComp, ecu.boostGearAxis, ' Gear', 'boost-duty-gear-comp-curve');
    generate1DHTMLTable('container-boost-target-gear-comp-curve', ecu.boostTargetGearComp, ecu.boostGearAxis, ' Gear', 'boost-target-gear-comp-curve');
}

// --- SELECTION & EDITING ---

function getCellCoords(td) {
    return { r: parseInt(td.getAttribute('data-r')), c: parseInt(td.getAttribute('data-c')) };
}

function getActiveTableData() {
    if (!ecu) return null;
    if (activeTableId === 've') return ecu.veTable;
    if (activeTableId === 'afr') return ecu.targetAfrTable;
    if (activeTableId === 'timing') return ecu.timingTable;
    if (activeTableId === 'vvt-intake') return ecu.vvtIntakeTable;
    if (activeTableId === 'vvt-exhaust') return ecu.vvtExhaustTable;
    if (activeTableId === 'wg-duty') return ecu.wgDutyTable;
    if (activeTableId === 'alt-volt') return ecu.altVoltTable;
    if (activeTableId === 'flex-ve') return ecu.flexVeTable;
    if (activeTableId === 'flex-afr') return ecu.flexAfrTable;
    if (activeTableId === 'flex-timing') return ecu.flexTimingTable;
    if (activeTableId === 'flex-boost') return ecu.flexBoostTable;
    if (activeTableId === 'flex-ve-curve') return [ecu.flexVeBlendCurve];
    if (activeTableId === 'flex-afr-curve') return [ecu.flexAfrBlendCurve];
    if (activeTableId === 'flex-timing-curve') return [ecu.flexTimingBlendCurve];
    if (activeTableId === 'flex-boost-curve') return [ecu.flexBoostBlendCurve];
    if (activeTableId === 'idle-rpm-curve')   return [ecu.idleTargetRpmCurve];
    if (activeTableId === 'idle-duty-curve')  return [ecu.idleDutyCurve];
    if (activeTableId === 'ect-fuel-curve')   return [ecu.ectFuelCurve];
    if (activeTableId === 'cranking-duty-curve') return [ecu.crankingIdleDutyCurve];
    if (activeTableId === 'post-crank-enrich-curve') return [ecu.postCrankEnrichCurve];
    if (activeTableId === 'iat-ve-curve')     return [ecu.iatVeCurve];
    if (activeTableId === 'inj-dt-curve')     return [ecu.injDtCurve];
    if (activeTableId === 'coil-dwell-curve') return [ecu.coilDwellCurve];
    if (activeTableId === 'iat-timing-curve') return [ecu.iatTimingCurve];
    if (activeTableId === 'knock-threshold-curve') return [ecu.knockThresholdCurve];
    if (activeTableId === 'accel-ms')  return ecu.accelMsTable;
    if (activeTableId === 'accel-map') return ecu.accelMapTable;
    if (activeTableId === 'cl-boost-target') return ecu.clBoostTargetTable;
    if (activeTableId === 'boost-duty-gear-comp-curve') return [ecu.boostDutyGearComp];
    if (activeTableId === 'boost-target-gear-comp-curve') return [ecu.boostTargetGearComp];
    return null;
}

function getTableDataById(tableId) {
    const saved = activeTableId;
    activeTableId = tableId;
    const data = getActiveTableData();
    activeTableId = saved;
    return data;
}

function saveUndoState() {
    if (!activeTableId) return;
    const tableData = getActiveTableData();
    if (!tableData) return;
    undoStack.push({ tableId: activeTableId, data: tableData.map(row => [...row]) });
    if (undoStack.length > MAX_UNDO) undoStack.shift();
    redoStack.length = 0;
}

function restoreTableData(tableId, snapshot) {
    const tableData = getTableDataById(tableId);
    if (!tableData) return;
    for (let r = 0; r < snapshot.length; r++) {
        for (let c = 0; c < snapshot[r].length; c++) {
            tableData[r][c] = snapshot[r][c];
        }
    }
    const suffix = tableId.endsWith('-curve') ? '' : '-table';
    const container = document.getElementById(`container-${tableId}${suffix}`);
    if (container) {
        snapshot.forEach((row, r) => {
            row.forEach((val, c) => {
                const td = container.querySelector(`td[data-r="${r}"][data-c="${c}"]`);
                if (td) td.innerText = val;
            });
        });
    }
    refreshHeatmap(tableId);
}

function undo() {
    if (!undoStack.length) return;
    const entry = undoStack.pop();
    const current = getTableDataById(entry.tableId);
    if (current) redoStack.push({ tableId: entry.tableId, data: current.map(row => [...row]) });
    restoreTableData(entry.tableId, entry.data);
}

function redo() {
    if (!redoStack.length) return;
    const entry = redoStack.pop();
    const current = getTableDataById(entry.tableId);
    if (current) undoStack.push({ tableId: entry.tableId, data: current.map(row => [...row]) });
    restoreTableData(entry.tableId, entry.data);
}

function getAxesForTable(tableId) {
    if (!ecu) return { rAxis: [], cAxis: [] };
    if (tableId === 'alt-volt') {
        return { rAxis: ecu.altVoltLoadAxis, cAxis: ecu.altVoltRpmAxis };
    }
    if (tableId === 'vvt-intake' || tableId === 'vvt-exhaust') {
        return { rAxis: ecu.vvtMapAxis, cAxis: ecu.vvtRpmAxis };
    }
    if (tableId === 'wg-duty') {
        return { rAxis: ecu.tpsAxis, cAxis: ecu.wgRpmAxis };
    }
    if (tableId === 'cl-boost-target') {
        return { rAxis: ecu.clBoostTpsAxis, cAxis: ecu.clBoostRpmAxis };
    }
    if (tableId === 'flex-boost') {
        return { rAxis: ecu.flexTpsAxis, cAxis: ecu.flexRpmAxis };
    }
    if (tableId === 'accel-ms') {
        return { rAxis: ecu.accelMsDtpsAxis, cAxis: ecu.accelMsRpmAxis };
    }
    if (tableId === 'accel-map') {
        return { rAxis: ecu.accelMapTpsAxis, cAxis: ecu.accelMapRpmAxis };
    }
    if (tableId && tableId.startsWith('flex-') && !tableId.endsWith('-curve')) {
        return { rAxis: ecu.flexMapAxis, cAxis: ecu.flexRpmAxis };
    }
    if (tableId === 'idle-rpm-curve') {
        return { rAxis: [0], cAxis: ecu.idleTargetRpmAxis };
    }
    if (tableId === 'idle-duty-curve') {
        return { rAxis: [0], cAxis: ecu.idleDutyCoolantAxis };
    }
    if (tableId === 'ect-fuel-curve') {
        return { rAxis: [0], cAxis: ecu.ectFuelAxis };
    }
    if (tableId === 'cranking-duty-curve') {
        return { rAxis: [0], cAxis: ecu.crankingIdleDutyAxis };
    }
    if (tableId === 'post-crank-enrich-curve') {
        return { rAxis: [0], cAxis: ecu.postCrankEnrichAxis };
    }
    if (tableId && tableId.endsWith('-curve')) {
        return { rAxis: [0], cAxis: ecu.ethXAxis };
    }
    return { rAxis: ecu.mapAxis, cAxis: ecu.rpmAxis };
}

function getActiveAxes() {
    return getAxesForTable(activeTableId);
}

function renderSelection() {
    document.querySelectorAll('.tune-table td').forEach(td => td.classList.remove('cell-selected'));
    if (!selStart || !selEnd || !activeTableId) return;

    const rMin = Math.min(selStart.r, selEnd.r);
    const rMax = Math.max(selStart.r, selEnd.r);
    const cMin = Math.min(selStart.c, selEnd.c);
    const cMax = Math.max(selStart.c, selEnd.c);
    
    const suffix = activeTableId.endsWith('-curve') ? '' : '-table';
    const tableContainer = document.getElementById(`container-${activeTableId}${suffix}`);
    if (!tableContainer) return;

    for (let r = rMin; r <= rMax; r++) {
        for (let c = cMin; c <= cMax; c++) {
            const td = tableContainer.querySelector(`td[data-r="${r}"][data-c="${c}"]`);
            if (td) td.classList.add('cell-selected');
        }
    }
}

const inlineEditor = document.getElementById('inline-editor');
const inlineInput = document.getElementById('inline-input');

function showInlineEditor(initialChar = "") {
    if (!selStart || !selEnd || !activeTableId || appMode !== 'garage') return;
    const rMin = Math.min(selStart.r, selEnd.r);
    const cMin = Math.min(selStart.c, selEnd.c);
    
    const suffix = activeTableId.endsWith('-curve') ? '' : '-table';
    const tableContainer = document.getElementById(`container-${activeTableId}${suffix}`);
    const topLeftTd = tableContainer.querySelector(`td[data-r="${rMin}"][data-c="${cMin}"]`);
    if (!topLeftTd) return;

    const rect = topLeftTd.getBoundingClientRect();
    if (inlineEditor) {
        inlineEditor.style.top = `${rect.top}px`;
        inlineEditor.style.left = `${rect.left}px`;
        inlineEditor.style.width = `${Math.max(rect.width, 40)}px`;
        inlineEditor.style.height = `${rect.height}px`;
        inlineEditor.classList.remove('hidden');
    }

    if (inlineInput) {
        inlineInput.value = initialChar;
        inlineInput.focus();
    }
}

function hideInlineEditor() {
    if (inlineEditor) inlineEditor.classList.add('hidden');
    if (inlineInput) inlineInput.blur();
    // If blur caused focus to land on another form element (e.g. a range slider),
    // move focus away so subsequent digit-key shortcuts reach the table handler.
    const active = document.activeElement;
    if (active && active !== document.body && active !== inlineInput &&
        (active.tagName === 'INPUT' || active.tagName === 'SELECT' || active.tagName === 'TEXTAREA')) {
        active.blur();
    }
    axisEditContext = null;
}

function applyEdit() {
    if (axisEditContext) {
        const val = parseFloat(inlineInput.value);
        if (!isNaN(val)) {
            const { type, idx, tableId } = axisEditContext;
            const axes = getAxesForTable(tableId);
            const axisArray = (type === 'col') ? axes.cAxis : axes.rAxis;
            axisArray[idx] = val;
            renderAllTables();
        }
        axisEditContext = null;
        hideInlineEditor();
        return;
    }

    let val = parseFloat(inlineInput.value);
    if (!isNaN(val) && selStart && selEnd && activeTableId) {
        const d = getTableDecimals(activeTableId);
        val = parseFloat(val.toFixed(d));
        if (activeTableId === 'cranking-duty-curve') val = Math.min(val, 100);
        saveUndoState();
        const rMin = Math.min(selStart.r, selEnd.r);
        const rMax = Math.max(selStart.r, selEnd.r);
        const cMin = Math.min(selStart.c, selEnd.c);
        const cMax = Math.max(selStart.c, selEnd.c);

        const tableData = getActiveTableData();
        const suffix = activeTableId.endsWith('-curve') ? '' : '-table';
        const tableContainer = document.getElementById(`container-${activeTableId}${suffix}`);

        for (let r = rMin; r <= rMax; r++) {
            for (let c = cMin; c <= cMax; c++) {
                if (activeTableId.endsWith('-curve')) {
                    tableData[0][c] = val;
                } else {
                    tableData[r][c] = val;
                }

                const td = tableContainer.querySelector(`td[data-r="${r}"][data-c="${c}"]`);
                if (td) {
                    td.innerText = d > 1 ? val.toFixed(d) : val;
                    td.style.backgroundColor = '#10B981';
                    setTimeout(() => td.style.backgroundColor = '', 300);
                }
            }
        }
        refreshHeatmap(activeTableId);
    }
    hideInlineEditor();
}

function interpolateSelection(mode) {
    if (!selStart || !selEnd || !activeTableId) return;
    saveUndoState();

    const rMin = Math.min(selStart.r, selEnd.r);
    const rMax = Math.max(selStart.r, selEnd.r);
    const cMin = Math.min(selStart.c, selEnd.c);
    const cMax = Math.max(selStart.c, selEnd.c);

    if (rMin === rMax && cMin === cMax) return;

    const tableData = getActiveTableData();
    const suffix = activeTableId.endsWith('-curve') ? '' : '-table';
    const tableContainer = document.getElementById(`container-${activeTableId}${suffix}`);
    const d = getTableDecimals(activeTableId);

    for (let r = rMin; r <= rMax; r++) {
        for (let c = cMin; c <= cMax; c++) {
            let val = (activeTableId.endsWith('-curve')) ? tableData[0][c] : tableData[r][c];

            if (mode === 'x' && cMax > cMin) {
                const valStart = (activeTableId.endsWith('-curve')) ? tableData[0][cMin] : tableData[r][cMin];
                const valEnd = (activeTableId.endsWith('-curve')) ? tableData[0][cMax] : tableData[r][cMax];
                val = valStart + (valEnd - valStart) * ((c - cMin) / (cMax - cMin));
            }
            else if (mode === 'y' && rMax > rMin && !activeTableId.endsWith('-curve')) {
                const valStart = tableData[rMin][c];
                const valEnd = tableData[rMax][c];
                val = valStart + (valEnd - valStart) * ((r - rMin) / (rMax - rMin));
            }
            else if (mode === '2d' && cMax > cMin && rMax > rMin && !activeTableId.endsWith('-curve')) {
                const c00 = tableData[rMin][cMin];
                const c01 = tableData[rMin][cMax];
                const c10 = tableData[rMax][cMin];
                const c11 = tableData[rMax][cMax];

                const tx = (c - cMin) / (cMax - cMin);
                const ty = (r - rMin) / (rMax - rMin);

                val = c00 * (1 - tx) * (1 - ty) +
                      c01 * tx * (1 - ty) +
                      c10 * (1 - tx) * ty +
                      c11 * tx * ty;
            }

            val = parseFloat(val.toFixed(d));
            if (activeTableId === 'cranking-duty-curve') val = Math.min(val, 100);
            if (activeTableId.endsWith('-curve')) tableData[0][c] = val;
            else tableData[r][c] = val;

            const td = tableContainer.querySelector(`td[data-r="${r}"][data-c="${c}"]`);
            if (td) {
                td.innerText = d > 1 ? val.toFixed(d) : val;
                td.style.backgroundColor = '#10B981';
                setTimeout(() => td.style.backgroundColor = '', 400);
            }
        }
    }
    refreshHeatmap(activeTableId);
}

function applyMathToSelection(operation) {
    if (!selStart || !selEnd || !activeTableId) return;
    saveUndoState();

    const mathValInput = document.getElementById('inp-math-val');
    const mathVal = mathValInput ? parseFloat(mathValInput.value) : NaN;
    if (isNaN(mathVal)) return;

    const rMin = Math.min(selStart.r, selEnd.r);
    const rMax = Math.max(selStart.r, selEnd.r);
    const cMin = Math.min(selStart.c, selEnd.c);
    const cMax = Math.max(selStart.c, selEnd.c);

    const tableData = getActiveTableData();
    const suffix = activeTableId.endsWith('-curve') ? '' : '-table';
    const tableContainer = document.getElementById(`container-${activeTableId}${suffix}`);
    const d = getTableDecimals(activeTableId);

    for (let r = rMin; r <= rMax; r++) {
        for (let c = cMin; c <= cMax; c++) {
            let currentVal = (activeTableId.endsWith('-curve')) ? tableData[0][c] : tableData[r][c];

            if (operation === '+') currentVal += mathVal;
            else if (operation === '-') currentVal -= mathVal;
            else if (operation === '*') currentVal *= mathVal;

            currentVal = parseFloat(currentVal.toFixed(d));
            if (activeTableId === 'cranking-duty-curve') currentVal = Math.min(currentVal, 100);
            if (activeTableId.endsWith('-curve')) tableData[0][c] = currentVal;
            else tableData[r][c] = currentVal;

            const td = tableContainer.querySelector(`td[data-r="${r}"][data-c="${c}"]`);
            if (td) {
                td.innerText = d > 1 ? currentVal.toFixed(d) : currentVal;
                td.style.backgroundColor = '#3B82F6';
                setTimeout(() => td.style.backgroundColor = '', 400);
            }
        }
    }
    refreshHeatmap(activeTableId);
}

function applyDeltaToSelection(delta) {
    if (!selStart || !selEnd || !activeTableId) return;
    saveUndoState();
    const rMin = Math.min(selStart.r, selEnd.r);
    const rMax = Math.max(selStart.r, selEnd.r);
    const cMin = Math.min(selStart.c, selEnd.c);
    const cMax = Math.max(selStart.c, selEnd.c);
    const tableData = getActiveTableData();
    const suffix = activeTableId.endsWith('-curve') ? '' : '-table';
    const tableContainer = document.getElementById(`container-${activeTableId}${suffix}`);
    const d = getTableDecimals(activeTableId);
    for (let r = rMin; r <= rMax; r++) {
        for (let c = cMin; c <= cMax; c++) {
            let v = (activeTableId.endsWith('-curve')) ? tableData[0][c] : tableData[r][c];
            v = parseFloat((v + delta).toFixed(d));
            if (activeTableId === 'cranking-duty-curve') v = Math.min(v, 100);
            if (activeTableId.endsWith('-curve')) tableData[0][c] = v;
            else tableData[r][c] = v;
            const td = tableContainer.querySelector(`td[data-r="${r}"][data-c="${c}"]`);
            if (td) {
                td.innerText = d > 1 ? v.toFixed(d) : v;
                td.style.backgroundColor = '#3B82F6';
                setTimeout(() => td.style.backgroundColor = '', 300);
            }
        }
    }
    refreshHeatmap(activeTableId);
}

// --- ENGINE VISUALS & SETTINGS ---

function setupEngineVisuals() {
    const viz = document.getElementById('engine-block-viz');
    if (!viz || !sampleEngine) return;

    const layout = sampleEngine.global_specs.engine_layout;
    const cyls = sampleEngine.global_specs.cylinders;
    
    viz.innerHTML = '';
    if (layout === "W") {
        // W engines: 4 banks (2 VR banks per side) — show as 4-column grid
        viz.className = "grid grid-cols-4 gap-x-3 gap-y-2 p-3 bg-gray-900 rounded-lg border border-gray-700 w-fit mx-auto";
    } else if (layout === "V" || layout === "Boxer" || layout === "VR") {
        // V / Boxer / VR: two banks, 2-column grid
        viz.className = "grid grid-cols-2 gap-x-6 gap-y-2 p-3 bg-gray-900 rounded-lg border border-gray-700 max-w-[120px] mx-auto";
    } else if (layout === "Rotary") {
        viz.className = "flex justify-center gap-4 p-3 bg-gray-900 rounded-lg border border-gray-700";
    } else {
        viz.className = "flex justify-center gap-2 p-3 bg-gray-900 rounded-lg border border-gray-700";
    }

    for (let i = 1; i <= cyls; i++) {
        const circle = document.createElement('div');
        circle.id = `cyl-${i}`;
        if (layout === "Rotary") {
            circle.className = "w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-b-[20px] border-b-gray-800 transition-colors duration-75 relative";
            const txt = document.createElement('span');
            txt.innerText = i;
            txt.className = "absolute top-[4px] -left-[3px] text-[8px] font-bold text-gray-500";
            circle.appendChild(txt);
        } else {
            circle.className = "w-6 h-6 rounded-full border-2 border-gray-600 bg-gray-800 flex items-center justify-center text-[10px] font-bold text-gray-500 transition-colors duration-75";
            circle.innerText = i;
        }
        viz.appendChild(circle);
    }

    updateMeltedCylinderDisplay();
    setupDistributorCap();
}

function setupDistributorCap() {
    const svg = document.getElementById('dist-cap-svg');
    const wrap = document.getElementById('distributor-cap-wrap');
    if (!svg || !wrap || !sampleEngine) return;

    const cyls = sampleEngine.global_specs.cylinders;
    const R = 42;       // terminal ring radius
    const DOT_R = 7;    // terminal circle radius

    svg.innerHTML = '';

    const ns = 'http://www.w3.org/2000/svg';

    // Outer cap ring
    const cap = document.createElementNS(ns, 'circle');
    cap.setAttribute('r', '52');
    cap.setAttribute('fill', '#1f2937');
    cap.setAttribute('stroke', '#4b5563');
    cap.setAttribute('stroke-width', '2');
    svg.appendChild(cap);

    // Center hub
    const hub = document.createElementNS(ns, 'circle');
    hub.setAttribute('r', '6');
    hub.setAttribute('fill', '#374151');
    hub.setAttribute('stroke', '#6b7280');
    hub.setAttribute('stroke-width', '1.5');
    svg.appendChild(hub);

    // Rotor arm (points from center to active terminal)
    const rotor = document.createElementNS(ns, 'line');
    rotor.id = 'dist-rotor';
    rotor.setAttribute('x1', '0');
    rotor.setAttribute('y1', '0');
    rotor.setAttribute('x2', '0');
    rotor.setAttribute('y2', String(-R));
    rotor.setAttribute('stroke', '#ef4444');
    rotor.setAttribute('stroke-width', '2.5');
    rotor.setAttribute('stroke-linecap', 'round');
    svg.appendChild(rotor);

    // Terminal dots — placed in firing order, clockwise starting from top
    for (let i = 0; i < cyls; i++) {
        const angleDeg = (i / cyls) * 360 - 90; // -90 so first terminal is at top
        const angleRad = angleDeg * Math.PI / 180;
        const x = R * Math.cos(angleRad);
        const y = R * Math.sin(angleRad);

        const dot = document.createElementNS(ns, 'circle');
        dot.id = `dist-term-${i}`;
        dot.setAttribute('cx', String(x));
        dot.setAttribute('cy', String(y));
        dot.setAttribute('r', String(DOT_R));
        dot.setAttribute('fill', '#374151');
        dot.setAttribute('stroke', '#6b7280');
        dot.setAttribute('stroke-width', '1.5');
        svg.appendChild(dot);

        const lbl = document.createElementNS(ns, 'text');
        lbl.setAttribute('x', String(x));
        lbl.setAttribute('y', String(y + 3.5));
        lbl.setAttribute('text-anchor', 'middle');
        lbl.setAttribute('font-size', '7');
        lbl.setAttribute('font-weight', 'bold');
        lbl.setAttribute('fill', '#9ca3af');
        lbl.textContent = String(firingOrderArr[i]);
        svg.appendChild(lbl);
    }

    // Show/hide based on current mode
    const isDistributor = ecu.ignitionMode === "distributor";
    wrap.classList.toggle('hidden', !isDistributor);
}

function updateMeltedCylinderDisplay() {
    if (!sampleEngine) return;
    const layout   = sampleEngine.global_specs.engine_layout;
    const cylCount = sampleEngine.global_specs.cylinders;
    for (let i = 1; i <= cylCount; i++) {
        const el = document.getElementById(`cyl-${i}`);
        if (!el) continue;
        if (ecu.disabledCylinders.has(i)) {
            // Permanently melted — dark maroon state
            if (layout === "Rotary") {
                el.className = el.className.replace(/border-b-\S+/, 'border-b-red-900');
            } else {
                el.className = "w-6 h-6 rounded-full border-2 border-red-800 bg-red-900 flex items-center justify-center text-[10px] font-bold text-red-400 transition-colors duration-75";
                el.style.boxShadow = '0 0 4px rgba(127, 29, 29, 0.8)';
            }
        } else {
            // Healthy — restore default appearance
            if (layout === "Rotary") {
                el.className = "w-0 h-0 border-l-[12px] border-l-transparent border-r-[12px] border-r-transparent border-b-[20px] border-b-gray-800 transition-colors duration-75 relative";
            } else {
                el.className = "w-6 h-6 rounded-full border-2 border-gray-600 bg-gray-800 flex items-center justify-center text-[10px] font-bold text-gray-500 transition-colors duration-75";
                el.style.boxShadow = 'none';
            }
        }
    }
}

function showCoilMeltWarning(cylList) {
    const toast = document.getElementById('coil-melt-toast');
    if (!toast) return;
    toast.innerText = `COIL MELT \u2014 Cylinder${cylList.length > 1 ? 's' : ''} ${cylList.join(', ')} disabled! Restart engine to repair.`;
    toast.classList.remove('hidden', 'opacity-0');
    toast.classList.add('opacity-100');
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(() => {
        toast.classList.replace('opacity-100', 'opacity-0');
        setTimeout(() => toast.classList.add('hidden'), 500);
    }, 5000);
}

// Populates all DOM input elements from the current ecu object (ECU → DOM).
// Call this before updateIdleSettings() whenever the ECU is loaded from an
// external source (binary or JSON) so that updateIdleSettings() (DOM → ECU)
// doesn't overwrite the freshly loaded values with stale DOM state.
function populateSettingsUI() {
    if (!ecu) return;
    const el = (id) => document.getElementById(id);
    const set = (id, v) => { const e = el(id); if (e) e.value = v; };
    const chk = (id, v) => { const e = el(id); if (e) e.checked = !!v; };

    // Idle
    set('inp-max-idle-tps', ecu.maxIdleTpsPct);
    set('inp-idle-rpm-upper-limit', ecu.idleRpmUpperLimit);
    chk('inp-idle-ign-en', ecu.idleIgnEnabled);
    set('inp-idle-p', ecu.idleIgnP);
    set('inp-idle-i', ecu.idleIgnI);
    set('inp-idle-d', ecu.idleIgnD);

    // Rev limit
    set('inp-rev-limit', ecu.revLimit);
    set('inp-rev-limit-hysteresis', ecu.revLimitHysteresis);
    chk('inp-rev-fuel-cut', ecu.revLimitFuelCut);
    chk('inp-rev-spark-cut', ecu.revLimitSparkCut);

    // Decel cut
    chk('inp-decel-cut', ecu.decelCutEnabled);
    set('inp-decel-delay', ecu.decelCutDelay);

    // Injector / fuel
    set('inp-inj-size', ecu.injectorSizeCc);
    initUnitLabels();
    set('inp-fuel-octane', ecu.fuelOctane);
    chk('inp-fuel-pressure-model', ecu.fuelPressureModelEnabled);
    set('inp-base-fuel-pressure', (typeof useMetric !== 'undefined' && !useMetric)
        ? parseFloat((ecu.baseFuelPressureKpa * 0.14504).toFixed(1))
        : ecu.baseFuelPressureKpa);
    set('inp-fuel-system-type', ecu.fuelSystemType);

    // Closed-loop fuel (values stored as fractions, shown as %)
    chk('inp-cl-enabled', ecu.closedLoopEnabled);
    set('inp-cl-deadband', (ecu.clDeadband * 100).toFixed(1));
    set('inp-cl-idle-tc', ecu.clIdleTimeConstant);
    set('inp-cl-idle-min', (ecu.clIdleMin * 100).toFixed(0));
    set('inp-cl-idle-max', (ecu.clIdleMax * 100).toFixed(0));
    set('inp-cl-run-tc', ecu.clRunningTimeConstant);
    set('inp-cl-run-min', (ecu.clRunningMin * 100).toFixed(0));
    set('inp-cl-run-max', (ecu.clRunningMax * 100).toFixed(0));
    set('inp-cl-wot-tc', ecu.clWotTimeConstant);
    set('inp-cl-wot-min', (ecu.clWotMin * 100).toFixed(0));
    set('inp-cl-wot-max', (ecu.clWotMax * 100).toFixed(0));
    chk('sim-o2-delay-enabled', ecu.o2DelayEnabled);

    // Cranking
    set('inp-crank-ve', ecu.crankingVe);
    set('inp-max-crank-rpm', ecu.maxCrankingRpm);
    set('inp-starter-crank-time', ecu.starterCrankTime);
    set('inp-crank-decay', ecu.crankingDecayTime);
    set('inp-post-crank-decay-cycles', ecu.postCrankDecayCycles);

    // VVL / VVT
    set('inp-cam-switch', ecu.camSwitchRpm);
    set('inp-cam-switch-tps', ecu.camSwitchTps);
    set('inp-vvl-fuel', ecu.vvlFuelAdder);
    set('inp-vvl-timing', ecu.vvlTimingAdder);
    const phaserEl = el('inp-vvt-phaser-enable');
    if (phaserEl) { phaserEl.checked = !!ecu.vvtPhaserEnabled; updateVvtPhaserUI(); }
    set('inp-vvt-kp', ecu.vvtKp);
    set('inp-vvt-ki', ecu.vvtKi);
    set('inp-vvt-kd', ecu.vvtKd);

    // Boost
    const _isImp = typeof useMetric !== 'undefined' && !useMetric;
    set('inp-wg-spring',      _isImp ? parseFloat((ecu.wgSpringKpa           * 0.14504).toFixed(1)) : ecu.wgSpringKpa);
    set('inp-boost-cut',      _isImp ? parseFloat((ecu.boostCutKpa           * 0.14504).toFixed(1)) : ecu.boostCutKpa);
    set('inp-boost-cut-hyst', _isImp ? parseFloat((ecu.boostCutHysteresisKpa * 0.14504).toFixed(1)) : ecu.boostCutHysteresisKpa);
    const clBoostEl = el('inp-cl-boost-enabled');
    if (clBoostEl) { clBoostEl.checked = !!ecu.clBoostEnabled; updateClBoostUI(); }
    set('inp-cl-boost-kp', ecu.clBoostKp);
    set('inp-cl-boost-ki', ecu.clBoostKi);
    set('inp-cl-boost-kd', ecu.clBoostKd);

    // Supercharger
    set('inp-sc-pulley', ecu.scPulleyRatio);
    set('inp-sc-bypass', ecu.scBypassThreshold);

    // Ignition / trigger
    set('inp-ign-mode', ecu.ignitionMode);
    const capWrap = document.getElementById('distributor-cap-wrap');
    if (capWrap) capWrap.classList.toggle('hidden', ecu.ignitionMode !== 'distributor');
    set('inp-spark-gap', ecu.sparkPlugGapIn);
    set('inp-trigger-offset', ecu.triggerAngleOffset);
    set('inp-fixed-timing', ecu.fixedTimingAngle);
    chk('inp-timing-light-mode', ecu.timingLightMode);

    // Accel enrichment
    set('inp-accel-mode', ecu.accelMode);
    set('inp-accel-time', ecu.accelTime);
    set('inp-accel-decay', ecu.accelDecayRate);

    // Knock control
    chk('inp-knk-enabled', ecu.knockEnabled);
    set('inp-knk-attack', ecu.knockAttackDeg);
    set('inp-knk-max-retard', ecu.knockMaxRetardDeg);
    set('inp-knk-recovery', ecu.knockRecoveryDegPerRev);
    set('inp-knk-min-tps', ecu.knockMinTps);
    set('inp-knk-min-map', ecu.knockMinMap);
    if (ecu.knockCenterFreq !== undefined) {
        set('inp-knk-center-freq', ecu.knockCenterFreq);
        set('inp-knk-center-freq-range', ecu.knockCenterFreq);
    }
    if (ecu.knockCalibrated !== undefined) {
        syncKnockCalibrationState();
        const knkParams = document.getElementById('knock-control-params');
        if (knkParams) knkParams.classList.toggle('hidden', !ecu.knockEnabled);
    }

    // Smart alternator
    chk('inp-smart-alt-en', ecu.smartAltEnabled);
    set('inp-smart-alt-p', ecu.smartAltP);
    set('inp-smart-alt-i', ecu.smartAltI);
    set('inp-smart-alt-d', ecu.smartAltD);

    // Launch control
    chk('inp-lc-enabled', ecu.lcEnabled);
    set('inp-lc-rpm', ecu.lcRpm);
    set('inp-lc-hyst', ecu.lcHysteresis);
    set('inp-lc-window', ecu.lcWindowRpm);
    set('inp-lc-cut', ecu.lcCutType);
    set('inp-lc-timing-limit', ecu.lcTimingAbsolute);
    set('inp-lc-timing-start', ecu.lcWindowStartTiming);
    set('inp-lc-fuel', ecu.lcFuelAdder);

    // Nitrous
    set('inp-nitrous-jet', ecu.nitrousJetSize);
    set('inp-nitrous-mode', ecu.nitrousActivationMode);
    set('inp-nitrous-fuel-jet', ecu.nitrousFuelJetSize);
    set('inp-nitrous-min-tps', ecu.nitrousMinTps);
    set('inp-nitrous-min-rpm', ecu.nitrousMinRpm);
    set('inp-nitrous-max-rpm', ecu.nitrousMaxRpm);
    set('inp-nitrous-max-map', ecu.nitrousMaxMapKpa);
    set('inp-nitrous-afr-cut', ecu.nitrousAfrCutLean);
    set('inp-nitrous-ve-comp', ecu.nitrousVeCompPct);
    set('inp-nitrous-retard', ecu.nitrousTimingRetard);
    ecu.nitrousCalculatedHp = Math.pow(ecu.nitrousJetSize || 0, 2) * 0.043;
    const hpLbl = document.getElementById('lbl-nitrous-hp');
    if (hpLbl) hpLbl.innerText = `≈ ${ecu.nitrousCalculatedHp.toFixed(1)} HP`;

    // Ethanol slider
    const flexSlider = el('inp-flex-slider');
    if (flexSlider) flexSlider.value = ecu.ethanolContent ?? 0;
    const flexLbl = document.getElementById('lbl-flex-val');
    if (flexLbl) flexLbl.textContent = `${ecu.ethanolContent ?? 0}%`;

    updateFuelPressureUI();
}

function updateIdleSettings() {
    if (!ecu) return;
    const elMaxIdleTps = document.getElementById('inp-max-idle-tps');
    if (elMaxIdleTps) ecu.maxIdleTpsPct = parseFloat(elMaxIdleTps.value) || 10;
    const elIdleRpmLimit = document.getElementById('inp-idle-rpm-upper-limit');
    if (elIdleRpmLimit) ecu.idleRpmUpperLimit = parseFloat(elIdleRpmLimit.value) || 200;

    const elRevLimit = document.getElementById('inp-rev-limit');
    if (elRevLimit) ecu.revLimit = parseFloat(elRevLimit.value) || 7200;

    const elRevHyst = document.getElementById('inp-rev-limit-hysteresis');
    if (elRevHyst) ecu.revLimitHysteresis = parseFloat(elRevHyst.value) || 200;

    const elRevFuel = document.getElementById('inp-rev-fuel-cut');
    if (elRevFuel) ecu.revLimitFuelCut = elRevFuel.checked;

    const elRevSpark = document.getElementById('inp-rev-spark-cut');
    if (elRevSpark) ecu.revLimitSparkCut = elRevSpark.checked;

    // Read coolant sim settings into globals (defined in main.js)
    if (typeof coolantSimEnabled !== 'undefined') {
        coolantSimEnabled = document.getElementById('sim-coolant-enabled')?.checked || false;
    }
    if (typeof useMetric !== 'undefined') {
        useMetric = document.getElementById('sim-unit-metric')?.checked === true;
        if (typeof tempUnitF !== 'undefined') tempUnitF = !useMetric;
    }
    if (typeof mapDisplayGauge !== 'undefined') {
        mapDisplayGauge = document.getElementById('sim-map-gauge')?.checked === true;
    }

    const elDecelCut = document.getElementById('inp-decel-cut');
    if (elDecelCut) ecu.decelCutEnabled = elDecelCut.checked;
    
    const elDecelDelay = document.getElementById('inp-decel-delay');
    if (elDecelDelay) ecu.decelCutDelay = parseFloat(elDecelDelay.value) || 0;
    
    const elInjSize = document.getElementById('inp-inj-size');
    if (elInjSize) ecu.injectorSizeCc = parseFloat(elInjSize.value) || 240;
    const elInjRefPressure = document.getElementById('inp-inj-ref-pressure');
    if (elInjRefPressure) {
        const _rawInjRef = parseFloat(elInjRefPressure.value) || 300;
        ecu.injectorReferencePressureKpa = (typeof useMetric !== 'undefined' && !useMetric)
            ? _rawInjRef / 0.14504
            : _rawInjRef;
    }

    ecu.closedLoopEnabled      = document.getElementById('inp-cl-enabled')?.checked ?? false;
    ecu.clDeadband             = (parseFloat(document.getElementById('inp-cl-deadband')?.value) || 2) / 100;
    ecu.clIdleTimeConstant     = parseFloat(document.getElementById('inp-cl-idle-tc')?.value) || 12;
    ecu.clIdleMin              = (parseFloat(document.getElementById('inp-cl-idle-min')?.value) || -25) / 100;
    ecu.clIdleMax              = (parseFloat(document.getElementById('inp-cl-idle-max')?.value) || 25) / 100;
    ecu.clRunningTimeConstant  = parseFloat(document.getElementById('inp-cl-run-tc')?.value) || 8;
    ecu.clRunningMin           = (parseFloat(document.getElementById('inp-cl-run-min')?.value) || -25) / 100;
    ecu.clRunningMax           = (parseFloat(document.getElementById('inp-cl-run-max')?.value) || 25) / 100;
    ecu.clWotTimeConstant      = parseFloat(document.getElementById('inp-cl-wot-tc')?.value) || 5;
    ecu.clWotMin               = (parseFloat(document.getElementById('inp-cl-wot-min')?.value) || -15) / 100;
    ecu.clWotMax               = (parseFloat(document.getElementById('inp-cl-wot-max')?.value) || 15) / 100;

    ecu.fuelPressureModelEnabled = document.getElementById('inp-fuel-pressure-model')?.checked ?? false;
    ecu.o2DelayEnabled = document.getElementById('sim-o2-delay-enabled')?.checked ?? false;
    { const _raw = parseFloat(document.getElementById('inp-base-fuel-pressure')?.value) || (useMetric ? 300 : 43.5);
      ecu.baseFuelPressureKpa = (typeof useMetric !== 'undefined' && !useMetric) ? _raw / 0.14504 : _raw; }
    ecu.fuelSystemType           = document.getElementById('inp-fuel-system-type')?.value || 'return';
    updateFuelPressureUI();

    const elFuelOctane = document.getElementById('inp-fuel-octane');
    if (elFuelOctane) ecu.fuelOctane = parseInt(elFuelOctane.value) || 91;
    
    const elIdleIgn = document.getElementById('inp-idle-ign-en');
    if (elIdleIgn) ecu.idleIgnEnabled = elIdleIgn.checked;
    
    const elIdleP = document.getElementById('inp-idle-p');
    if (elIdleP) ecu.idleIgnP = parseFloat(elIdleP.value) || 0;
    
    const elIdleI = document.getElementById('inp-idle-i');
    if (elIdleI) ecu.idleIgnI = parseFloat(elIdleI.value) || 0;
    
    const elIdleD = document.getElementById('inp-idle-d');
    if (elIdleD) ecu.idleIgnD = parseFloat(elIdleD.value) || 0;
    
    const elCrankVe = document.getElementById('inp-crank-ve');
    if (elCrankVe) ecu.crankingVe = parseFloat(elCrankVe.value) || 50;
    
    const elMaxCrankRpm = document.getElementById('inp-max-crank-rpm');
    if (elMaxCrankRpm) ecu.maxCrankingRpm = parseInt(elMaxCrankRpm.value) || 300;

    const elStarterCrankTime = document.getElementById('inp-starter-crank-time');
    if (elStarterCrankTime) ecu.starterCrankTime = parseFloat(elStarterCrankTime.value) || 2.0;

    if (typeof starterMode !== 'undefined') {
        const elStarterMode = document.querySelector('input[name="sim-starter-mode"]:checked');
        if (elStarterMode) starterMode = elStarterMode.value;
    }

    const elCrankDecay = document.getElementById('inp-crank-decay');
    if (elCrankDecay) ecu.crankingDecayTime = parseFloat(elCrankDecay.value) || 3.0;

    const elPcdc = document.getElementById('inp-post-crank-decay-cycles');
    if (elPcdc) ecu.postCrankDecayCycles = parseInt(elPcdc.value) || 50;

    const elCamSwitch = document.getElementById('inp-cam-switch');
    if (elCamSwitch) ecu.camSwitchRpm = parseFloat(elCamSwitch.value) || 5500;
    
    const elCamSwitchTps = document.getElementById('inp-cam-switch-tps');
    if (elCamSwitchTps) ecu.camSwitchTps = parseFloat(elCamSwitchTps.value) || 20;
    
    const elVvlFuel = document.getElementById('inp-vvl-fuel');
    if (elVvlFuel) ecu.vvlFuelAdder = parseFloat(elVvlFuel.value) || 0;
    
    const elVvlTiming = document.getElementById('inp-vvl-timing');
    if (elVvlTiming) ecu.vvlTimingAdder = parseFloat(elVvlTiming.value) || 0;
    
    const _impBoost = typeof useMetric !== 'undefined' && !useMetric;
    const elWgSpring = document.getElementById('inp-wg-spring');
    if (elWgSpring) { const v = parseFloat(elWgSpring.value) || (_impBoost ? 7.0 : 48.3); ecu.wgSpringKpa = _impBoost ? v / 0.14504 : v; }
    const elBoostCut = document.getElementById('inp-boost-cut');
    if (elBoostCut) { const v = parseFloat(elBoostCut.value) || (_impBoost ? 15.0 : 103.4); ecu.boostCutKpa = _impBoost ? v / 0.14504 : v; }
    const elBoostCutHyst = document.getElementById('inp-boost-cut-hyst');
    if (elBoostCutHyst) { const v = parseFloat(elBoostCutHyst.value) ?? (_impBoost ? 2.0 : 13.8); ecu.boostCutHysteresisKpa = _impBoost ? v / 0.14504 : v; }
    ecu.clBoostEnabled = document.getElementById('inp-cl-boost-enabled')?.checked ?? false;
    ecu.clBoostKp = parseFloat(document.getElementById('inp-cl-boost-kp')?.value) || 0.8;
    ecu.clBoostKi = parseFloat(document.getElementById('inp-cl-boost-ki')?.value) || 0.15;
    ecu.clBoostKd = parseFloat(document.getElementById('inp-cl-boost-kd')?.value) || 0.05;
    if (typeof updateClBoostUI === 'function') updateClBoostUI();
    const elScPulley = document.getElementById('inp-sc-pulley');
    if (elScPulley) {
        ecu.scPulleyRatio = parseFloat(elScPulley.value) || 2.5;
        // Update SC RPM display
        const scRpmEl = document.getElementById('sc-rpm-display');
        if (scRpmEl) scRpmEl.textContent = Math.round((ecu.revLimit || 7200) * ecu.scPulleyRatio).toLocaleString() + ' RPM';
    }
    const elScBypass = document.getElementById('inp-sc-bypass');
    if (elScBypass) ecu.scBypassThreshold = parseFloat(elScBypass.value) ?? 15.0;


    const elDisplayLambda = document.getElementById('inp-display-lambda');
    if (elDisplayLambda) ecu.displayLambda = elDisplayLambda.checked;

    const elFlexSlider = document.getElementById('inp-flex-slider');
    if (elFlexSlider) ecu.ethanolContent = parseFloat(elFlexSlider.value) || 0;

    // Launch Control Settings
    const elLcEnabled = document.getElementById('inp-lc-enabled');
    if (elLcEnabled) ecu.lcEnabled = elLcEnabled.checked;

    const elLcRpm = document.getElementById('inp-lc-rpm');
    if (elLcRpm) {
        const val = parseFloat(elLcRpm.value);
        ecu.lcRpm = isNaN(val) ? 4500 : val;
    }

    const elLcHyst = document.getElementById('inp-lc-hyst');
    if (elLcHyst) {
        const val = parseFloat(elLcHyst.value);
        ecu.lcHysteresis = isNaN(val) ? 100 : val;
    }

    const elLcWindow = document.getElementById('inp-lc-window');
    if (elLcWindow) {
        const val = parseFloat(elLcWindow.value);
        ecu.lcWindowRpm = isNaN(val) ? 500 : val;
    }

    const elLcCut = document.getElementById('inp-lc-cut');
    if (elLcCut) ecu.lcCutType = elLcCut.value || "Fuel";

    const elLcTimingStart = document.getElementById('inp-lc-timing-start');
    if (elLcTimingStart) {
        const val = parseFloat(elLcTimingStart.value);
        ecu.lcWindowStartTiming = isNaN(val) ? 10 : val;
    }

    const elLcTimingLimit = document.getElementById('inp-lc-timing-limit');
    if (elLcTimingLimit) {
        const val = parseFloat(elLcTimingLimit.value);
        ecu.lcTimingAbsolute = isNaN(val) ? -15 : val;
    }

    const elLcFuel = document.getElementById('inp-lc-fuel');
    if (elLcFuel) {
        const val = parseFloat(elLcFuel.value);
        ecu.lcFuelAdder = isNaN(val) ? 5 : val;
    }

    const elLcSwitch = document.getElementById('inp-lc-switch');
    if (elLcSwitch) ecu.twoStepSwitchActive = elLcSwitch.checked;

    // Update Drag Strip UI if it exists
    const dragLaunchInput = document.getElementById('drag-launch-rpm');
    if (dragLaunchInput) {
        if (ecu.lcEnabled) {
            dragLaunchInput.value = ecu.lcRpm;
            dragLaunchInput.disabled = true;
            dragLaunchInput.classList.add('opacity-50', 'cursor-not-allowed');
        } else {
            dragLaunchInput.disabled = false;
            dragLaunchInput.classList.remove('opacity-50', 'cursor-not-allowed');
        }
    }
    
    // Smart Alternator
    const elSmartAlt = document.getElementById('inp-smart-alt-en');
    if (elSmartAlt) ecu.smartAltEnabled = elSmartAlt.checked;
    const elSAP = document.getElementById('inp-smart-alt-p');
    if (elSAP) ecu.smartAltP = parseFloat(elSAP.value) || 25;
    const elSAI = document.getElementById('inp-smart-alt-i');
    if (elSAI) ecu.smartAltI = parseFloat(elSAI.value) || 2;
    const elSAD = document.getElementById('inp-smart-alt-d');
    if (elSAD) ecu.smartAltD = parseFloat(elSAD.value) || 0;

    // Nitrous Oxide System (nitrousEnabled and nitrousShotType are driven by Upgrades & Mods)
    const elN2oJet = document.getElementById('inp-nitrous-jet');
    if (elN2oJet) {
        ecu.nitrousJetSize = parseFloat(elN2oJet.value) || 28;
        ecu.nitrousCalculatedHp = Math.pow(ecu.nitrousJetSize, 2) * 0.043;
        const hpLbl = document.getElementById('lbl-nitrous-hp');
        if (hpLbl) hpLbl.innerText = `≈ ${ecu.nitrousCalculatedHp.toFixed(1)} HP`;
    }
    const elN2oMode = document.getElementById('inp-nitrous-mode');
    if (elN2oMode) ecu.nitrousActivationMode = elN2oMode.value;
    const elN2oFuelJet = document.getElementById('inp-nitrous-fuel-jet');
    if (elN2oFuelJet) ecu.nitrousFuelJetSize = parseFloat(elN2oFuelJet.value) || 28;
    const elN2oMinTps = document.getElementById('inp-nitrous-min-tps');
    if (elN2oMinTps) ecu.nitrousMinTps = parseFloat(elN2oMinTps.value) || 80;
    const elN2oMinRpm = document.getElementById('inp-nitrous-min-rpm');
    if (elN2oMinRpm) ecu.nitrousMinRpm = parseFloat(elN2oMinRpm.value) || 2500;
    const elN2oMaxRpm = document.getElementById('inp-nitrous-max-rpm');
    if (elN2oMaxRpm) ecu.nitrousMaxRpm = parseFloat(elN2oMaxRpm.value) || 7000;
    const elN2oMaxMap = document.getElementById('inp-nitrous-max-map');
    if (elN2oMaxMap) ecu.nitrousMaxMapKpa = parseFloat(elN2oMaxMap.value) || 150;
    const elN2oAfrCut = document.getElementById('inp-nitrous-afr-cut');
    if (elN2oAfrCut) ecu.nitrousAfrCutLean = parseFloat(elN2oAfrCut.value) || 13.5;
    const elN2oVeComp = document.getElementById('inp-nitrous-ve-comp');
    if (elN2oVeComp) ecu.nitrousVeCompPct = parseFloat(elN2oVeComp.value) || 0;
    const elN2oRetard = document.getElementById('inp-nitrous-retard');
    if (elN2oRetard) ecu.nitrousTimingRetard = parseFloat(elN2oRetard.value) || 0;
    updateNitrousUI();

    // Ignition mode and plug gap
    const elIgnMode = document.getElementById('inp-ign-mode');
    if (elIgnMode) ecu.ignitionMode = elIgnMode.value;
    const elSparkGap = document.getElementById('inp-spark-gap');
    if (elSparkGap) ecu.sparkPlugGapIn = parseFloat(elSparkGap.value) || 0.040;

    // Trigger Calibration Settings
    const elTrigOffset = document.getElementById('inp-trigger-offset');
    if (elTrigOffset) ecu.triggerAngleOffset = parseFloat(elTrigOffset.value) || 0;
    const elFixedTiming = document.getElementById('inp-fixed-timing');
    if (elFixedTiming) ecu.fixedTimingAngle = parseFloat(elFixedTiming.value) || 10;
    const elTLM = document.getElementById('inp-timing-light-mode');
    if (elTLM) ecu.timingLightMode = elTLM.checked;

    // Acceleration Enrichment Settings
    const elAccelMode  = document.getElementById('inp-accel-mode');
    if (elAccelMode)  ecu.accelMode = elAccelMode.value || 'none';
    const elAccelTime  = document.getElementById('inp-accel-time');
    if (elAccelTime)  ecu.accelTime = parseFloat(elAccelTime.value) || 0.5;
    const elAccelDecay = document.getElementById('inp-accel-decay');
    if (elAccelDecay) ecu.accelDecayRate = parseFloat(elAccelDecay.value) || 2.0;

    // Knock Control
    const elKnkEn  = document.getElementById('inp-knk-enabled');
    if (elKnkEn)  ecu.knockEnabled = elKnkEn.checked;
    const elKnkAtk = document.getElementById('inp-knk-attack');
    if (elKnkAtk) ecu.knockAttackDeg = parseFloat(elKnkAtk.value) || 2.0;
    const elKnkMax = document.getElementById('inp-knk-max-retard');
    if (elKnkMax) ecu.knockMaxRetardDeg = parseFloat(elKnkMax.value) || 10.0;
    const elKnkRec = document.getElementById('inp-knk-recovery');
    if (elKnkRec) ecu.knockRecoveryDegPerRev = parseFloat(elKnkRec.value) || 0.5;
    const elKnkMinTps = document.getElementById('inp-knk-min-tps');
    if (elKnkMinTps) ecu.knockMinTps = parseFloat(elKnkMinTps.value) ?? 25.0;
    const elKnkMinMap = document.getElementById('inp-knk-min-map');
    if (elKnkMinMap) ecu.knockMinMap = parseFloat(elKnkMinMap.value) ?? 60.0;
    const elKnkCenterFreq = document.getElementById('inp-knk-center-freq');
    if (elKnkCenterFreq) ecu.knockCenterFreq = parseFloat(elKnkCenterFreq.value) || 7.0;

    // Visual confirmation on Apply Buttons
    document.querySelectorAll('.setting-apply-btn').forEach(btn => {
        if (btn.closest('.active')) {
            btn.innerText = T('applied', 'Applied!');
            btn.classList.add('bg-green-600');
            setTimeout(() => { btn.innerText = T('apply_settings', 'Apply Settings'); btn.classList.remove('bg-green-600'); }, 1000);
        }
    });

    // Clear injector hint if mismatch is now resolved
    const _injHint = document.getElementById('injector-hint');
    if (_injHint && typeof physics !== 'undefined' && physics?.installedInjectorSizeCc && ecu) {
        const mismatch = Math.abs(physics.installedInjectorSizeCc / ecu.injectorSizeCc - 1.0) >= 0.05;
        if (!mismatch) _injHint.classList.add('hidden');
    }
}

function updateVvtPhaserUI() {
    const enabled = document.getElementById('inp-vvt-phaser-enable')?.checked;
    const params = document.getElementById('vvt-phaser-params');
    if (params) params.classList.toggle('hidden', !enabled);
}

function updateFuelPressureUI() {
    const enabled = document.getElementById('inp-fuel-pressure-model')?.checked;
    const params  = document.getElementById('fuel-pressure-params');
    const rowFP   = document.getElementById('row-fuel-pressure');
    const rowPD   = document.getElementById('row-pump-demand');
    if (params) params.classList.toggle('hidden', !enabled);
    if (rowFP)  rowFP.classList.toggle('hidden', !enabled);
    if (rowPD)  rowPD.classList.add('hidden'); // Always hidden from live telemetry
}

function updateVvtPhaserSettings() {
    if (!ecu) return;
    const elEnable = document.getElementById('inp-vvt-phaser-enable');
    ecu.vvtPhaserEnabled = elEnable ? elEnable.checked : false;
    const elKp = document.getElementById('inp-vvt-kp');
    ecu.vvtKp = elKp ? (parseFloat(elKp.value) || 8.0) : 8.0;
    const elKi = document.getElementById('inp-vvt-ki');
    ecu.vvtKi = elKi ? (parseFloat(elKi.value) || 2.0) : 2.0;
    const elKd = document.getElementById('inp-vvt-kd');
    ecu.vvtKd = elKd ? (parseFloat(elKd.value) || 0.3) : 0.3;

    // Visual confirmation
    const btn = document.querySelector('#vvt-phaser-apply-btn');
    if (btn) {
        btn.innerText = T('applied', 'Applied!');
        btn.classList.add('bg-green-600');
        setTimeout(() => { btn.innerText = T('apply_settings', 'Apply Settings'); btn.classList.remove('bg-green-600'); }, 1000);
    }
}

function updateNitrousUI() {
    const isInstalled = ecu.nitrousEnabled;
    const isEcuMode = ecu.nitrousActivationMode === 'ecu';
    const isWetShot = ecu.nitrousShotType === 'wet';
    const showButton = isInstalled && !isEcuMode;

    // "Not installed" message vs. controls
    const notInstalledMsg = document.getElementById('nitrous-not-installed');
    const nitrousControls = document.getElementById('nitrous-controls');
    if (notInstalledMsg) notInstalledMsg.classList.toggle('hidden', isInstalled);
    if (nitrousControls) nitrousControls.classList.toggle('hidden', !isInstalled);

    // Fuel jet input: visible for wet systems regardless of activation mode
    const wetFuelJet = document.getElementById('nitrous-wet-fuel-jet');
    if (wetFuelJet) wetFuelJet.classList.toggle('hidden', !isWetShot);

    // Dry warning: switch mode + dry system only
    const dryWarning = document.getElementById('nitrous-dry-warning');
    if (dryWarning) dryWarning.classList.toggle('hidden', isWetShot || isEcuMode);

    // ECU control section
    const ecuSection = document.getElementById('nitrous-ecu-section');
    if (ecuSection) ecuSection.classList.toggle('hidden', !isEcuMode);

    // N2O switch button in dyno and drag views
    document.querySelectorAll('.btn-nitrous-switch').forEach(btn => {
        btn.classList.toggle('hidden', !showButton);
    });

    // Gray out the Nitrous System nav entry when no kit is installed (same pattern as boost tabs)
    const nitrousNavBtn = document.getElementById('btn-nav-nitrous');
    if (nitrousNavBtn) {
        nitrousNavBtn.disabled = !isInstalled;
        nitrousNavBtn.style.opacity = isInstalled ? '1.0' : '0.3';
        nitrousNavBtn.style.cursor = isInstalled ? 'pointer' : 'not-allowed';
    }
}

function updateVVTMenuVisibility() {
    if (!sampleEngine) return;
    const vvtMode = sampleEngine.vvt_parameters?.vvt_mode || "None";
    const hasHighCam = !!sampleEngine.performance_maps["High Cam Profile"];
    
    const btnIntake = document.getElementById('btn-nav-vvt-intake');
    const btnExhaust = document.getElementById('btn-nav-vvt-exhaust');
    const btnSettings = document.getElementById('btn-nav-vvt-settings');
    
    const enableIntake = (vvtMode === "Intake" || vvtMode === "Dual");
    const enableExhaust = (vvtMode === "Dual");
    const enableSettings = (vvtMode !== "None" || hasHighCam);

    const activeClass = "text-gray-300 hover:bg-gray-600 hover:text-white";
    const disabledClass = "text-gray-600 cursor-not-allowed opacity-50 bg-transparent hover:bg-transparent hover:text-gray-600";

    if (btnIntake) {
        btnIntake.disabled = !enableIntake;
        btnIntake.className = `w-full text-left px-4 py-2 text-sm ${enableIntake ? activeClass : disabledClass}`;
    }
    if (btnExhaust) {
        btnExhaust.disabled = !enableExhaust;
        btnExhaust.className = `w-full text-left px-4 py-2 text-sm ${enableExhaust ? activeClass : disabledClass}`;
    }
    if (btnSettings) {
        btnSettings.disabled = !enableSettings;
        btnSettings.className = `w-full text-left px-4 py-2 text-sm ${enableSettings ? activeClass : disabledClass}`;
    }

    // Fallback: if user is on a disabled tab, kick them back to the VE Table
    if (!enableIntake && document.getElementById('tab-vvt-intake')?.classList.contains('active')) switchTab('tab-ve', document.querySelector('.tab-dropdown-btn'));
    if (!enableExhaust && document.getElementById('tab-vvt-exhaust')?.classList.contains('active')) switchTab('tab-ve', document.querySelector('.tab-dropdown-btn'));
    if (!enableSettings && document.getElementById('tab-vvt-settings')?.classList.contains('active')) switchTab('tab-ve', document.querySelector('.tab-dropdown-btn'));
}

function onTimingLightModeToggle() {
    const checkbox = document.getElementById('inp-timing-light-mode');
    const isActive = checkbox ? checkbox.checked : false;
    ecu.timingLightMode = isActive;

    if (!isActive) {
        dismissTriggerCalModal();
        _trigCalPrevCorrect = false;
    }

    const main = document.getElementById('trigger-main');
    if (main) {
        main.classList.toggle('opacity-40', !isActive);
        main.classList.toggle('pointer-events-none', !isActive);
    }
    const dial = document.getElementById('trigger-dial');
    if (dial) {
        dial.classList.toggle('opacity-40', !isActive);
        dial.classList.toggle('pointer-events-none', !isActive);
    }

    const btn = document.getElementById('btn-power');
    if (btn) {
        btn.disabled = isActive;
        btn.classList.toggle('opacity-50', isActive);
        btn.classList.toggle('cursor-not-allowed', isActive);
        btn.title = isActive ? 'Disabled: Timing Light Mode is active' : '';
    }

    if (isActive) {
        lastCrankTimestamp = null;
        crankAnimFrame = requestAnimationFrame(animateCrankMark);
    } else {
        if (crankAnimFrame) { cancelAnimationFrame(crankAnimFrame); crankAnimFrame = null; }
        lastCrankTimestamp = null;
        const inner = document.getElementById('crank-inner');
        if (inner) inner.removeAttribute('transform');
        const mark = document.getElementById('crank-mark');
        if (mark) mark.setAttribute('fill', '#EF4444');
    }
}

function animateCrankMark(timestamp) {
    if (!ecu.timingLightMode) { crankAnimFrame = null; return; }

    const crankRpm = 30;

    // Delta-time rotation — single source of truth for both visual and flash detection
    const dt = lastCrankTimestamp !== null ? (timestamp - lastCrankTimestamp) : 0;
    if (dt > 0) {
        cumulativeAngle = (cumulativeAngle + dt * crankRpm * 360 / 60000) % 360;
    }
    lastCrankTimestamp = timestamp;

    // Drive rotation directly — no CSS animation involved
    const inner = document.getElementById('crank-inner');
    if (inner) inner.setAttribute('transform', `rotate(${cumulativeAngle}, 150, 150)`);

    const elOff = document.getElementById('inp-trigger-offset');
    const elFix = document.getElementById('inp-fixed-timing');
    const ecuOffset   = elOff ? (parseFloat(elOff.value) || 0) : (ecu.triggerAngleOffset ?? 0);
    const fixedTiming = elFix ? (parseFloat(elFix.value) || 0) : (ecu.fixedTimingAngle   ?? 10);
    const engineBase  = window.engineTriggerBaseOffset ?? 0;

    // BTDC → clockwise crank angle
    const rawSpark = fixedTiming + (engineBase - ecuOffset);
    const sparkBtdc = ((rawSpark % 360) + 360) % 360;
    const sparkAngle = (360 - sparkBtdc) % 360;

    // Green within ±8° of firing position, handles 0°/360° wrap
    const rawDiff = Math.abs(cumulativeAngle - sparkAngle);
    const isFiring = Math.min(rawDiff, 360 - rawDiff) < 8;

    const mark = document.getElementById('crank-mark');
    if (mark) mark.setAttribute('fill', isFiring ? '#10B981' : '#EF4444');

    crankAnimFrame = requestAnimationFrame(animateCrankMark);
}

function updateTriggerVisual() {
    lastCrankTimestamp = null; // prevent delta-time jump when settings change

    if (!ecu.timingLightMode) return;

    const ecuOffset  = parseFloat(document.getElementById('inp-trigger-offset')?.value) || 0;
    const engineBase = window.engineTriggerBaseOffset ?? 0;
    const isCorrect  = Math.abs(ecuOffset - engineBase) <= 2;

    if (isCorrect && !_trigCalPrevCorrect) {
        const toast = document.getElementById('trigger-cal-success');
        if (toast) toast.style.display = 'flex';
    }
    _trigCalPrevCorrect = isCorrect;
}

function dismissTriggerCalModal() {
    const toast = document.getElementById('trigger-cal-success');
    if (toast) toast.style.display = 'none';
}

function confirmTriggerCalibration() {
    dismissTriggerCalModal();
    updateIdleSettings();
}

// --- UPGRADES & MODS ---

function createTwinTurboBlock() {
    const wrapper = document.createElement('div');
    wrapper.id = 'twin-turbo-offer';
    wrapper.className = 'hidden mt-2 border border-gray-600 bg-gray-800/50 rounded p-3';
    wrapper.innerHTML = `
        <label class="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" id="chk-twin-turbo"
                   class="rounded bg-gray-900 border-gray-600 text-orange-500 focus:ring-orange-500">
            <span class="text-sm text-gray-200 font-bold">${T('twin_turbo_setup', 'Twin Turbo Setup')}</span>
            <span class="text-xs text-gray-400">${T('twin_turbo_help', '(&times;1.9 airflow, ~&frac12;&times; spool rate)')}</span>
        </label>
    `;
    wrapper.querySelector('#chk-twin-turbo').addEventListener('change', (e) => {
        twinTurboEnabled = e.target.checked;
        applyVehicleMods();
    });
    return wrapper;
}

function renderUpgradesUI(modsToForceCheck = null) {
    const container = document.getElementById('upgrades-list');
    if (!container) return;

    // Snapshot current selections before rebuilding
    const col1 = ['Intake', 'Intake Manifold', 'Exhaust Manifold', 'Exhaust', 'Port and Polish', 'Turbocharger', 'Supercharger', 'Camshaft'];
    const col2 = ['Pistons', 'Rods', 'Clutch', 'Cooling', 'FuelPump', 'Injector'];
    const allCats = [...col1, ...col2];
    const prevSelections = {};
    allCats.forEach(cat => {
        const sel = document.getElementById(`mod-select-${cat.toLowerCase()}`);
        if (sel) prevSelections[cat] = sel.value;
    });
    const prevNitrous = document.getElementById('mod-select-nitrous')?.value || '';

    container.innerHTML = '';
    
    const renderCategory = (cat) => {
        const groupDiv = document.createElement('div');
        groupDiv.className = 'border-b border-gray-700 pb-4 mb-2';
        
        let displayLabel = cat;
        if (cat === 'Intake') displayLabel = T('mod_intake', 'Intake');
        if (cat === 'Intake Manifold') displayLabel = T('mod_intake_manifold', 'Intake Manifold');
        if (cat === 'Exhaust Manifold') displayLabel = T('mod_exhaust_manifold', 'Exhaust Manifold');
        if (cat === 'Exhaust') displayLabel = T('mod_exhaust', 'Exhaust');
        if (cat === 'Port and Polish') displayLabel = T('mod_port_and_polish', 'Port and Polish');
        if (cat === 'Turbocharger') displayLabel = T('mod_turbocharger', 'Turbocharger');
        if (cat === 'Supercharger') displayLabel = T('mod_supercharger', 'Supercharger');
        if (cat === 'Camshaft') displayLabel = T('mod_camshaft', 'Camshaft');
        if (cat === 'Pistons') displayLabel = T('mod_pistons', 'Pistons');
        if (cat === 'Rods') displayLabel = T('mod_rods', 'Rods');
        if (cat === 'Clutch') displayLabel = T('mod_clutch', 'Clutch');
        if (cat === 'Cooling') displayLabel = T('mod_intercooler', 'Intercooler');
        if (cat === 'FuelPump') displayLabel = T('mod_fuel_pump', 'Fuel Pump');
        if (cat === 'Injector') displayLabel = T('mod_fuel_injector', 'Fuel Injector');
        
        groupDiv.innerHTML = `<h4 class="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">${displayLabel}</h4>`;
        
        const select = document.createElement('select');
        select.id = `mod-select-${cat.toLowerCase()}`;
        select.className = 'w-full bg-gray-900 border border-gray-600 rounded p-2 text-white text-sm outline-none focus:border-blue-500';
        select.innerHTML = `<option value="">${T('stock_none', 'Stock / None')}</option>`;
        
        // Add hosted mods for this category.
        // If the mod has already been fetched into sampleModsDb, use mod_id as the value
        // (so applyVehicleMods can find it) rather than the raw URL — prevents duplicates.
        const coveredModIds = new Set();
        hostedMods.filter(m => m.type === cat).forEach(m => {
            const fetched = Object.values(sampleModsDb).find(db => db.display_name === m.name);
            if (fetched) {
                select.innerHTML += `<option value="${fetched.mod_id}">${m.name}</option>`;
                coveredModIds.add(fetched.mod_id);
            } else {
                select.innerHTML += `<option value="${m.url}">${m.name}</option>`;
            }
        });

        // Add any custom-imported mods not already covered by a hosted entry above
        for (const modId in sampleModsDb) {
            if (coveredModIds.has(modId)) continue;
            const mod = sampleModsDb[modId];
            let mType = (mod.mod_type === 'Intercooler') ? 'Cooling' : mod.mod_type;
            if (mType === 'Intake/Exhaust/Head') mType = 'Intake'; // Legacy fallback

            if (mType === cat) {
                if (!select.querySelector(`option[value="${mod.mod_id}"]`)) {
                    select.innerHTML += `<option value="${mod.mod_id}">${mod.display_name}</option>`;
                }
            }
        }

        select.addEventListener('change', (e) => {
            const val = e.target.value;
            if (val.includes('.xsim-mod')) {
                if (typeof fetchMod === 'function') fetchMod(val, cat);
            } else {
                applyVehicleMods();
            }
            if (cat === 'Turbocharger') {
                const offerDiv = document.getElementById('twin-turbo-offer');
                if (offerDiv) offerDiv.classList.toggle('hidden', !val || val.includes('.xsim-mod'));
                if (!val) { twinTurboEnabled = false; const chk = document.getElementById('chk-twin-turbo'); if (chk) chk.checked = false; }
            }
        });

        groupDiv.appendChild(select);
        return groupDiv;
    };

    const leftDiv = document.createElement('div');
    col1.forEach(cat => {
        leftDiv.appendChild(renderCategory(cat));
        if (cat === 'Turbocharger') leftDiv.appendChild(createTwinTurboBlock());
    });
    container.appendChild(leftDiv);

    const rightDiv = document.createElement('div');
    col2.forEach(cat => rightDiv.appendChild(renderCategory(cat)));

    // Nitrous System — gated by purchase (has-nitrous Shopify tag / window.hasNitrous)
    const hasNitrous = typeof window.hasNitrous !== 'undefined' ? window.hasNitrous : true;
    const nitrousDiv = document.createElement('div');
    nitrousDiv.className = 'border-b border-gray-700 pb-4 mb-2';
    nitrousDiv.innerHTML = `<h4 class="text-xs font-bold text-gray-400 uppercase tracking-widest mb-2">${T('nitrous_system', 'Nitrous System')}</h4>`;
    if (hasNitrous) {
        const nitrousSelect = document.createElement('select');
        nitrousSelect.id = 'mod-select-nitrous';
        nitrousSelect.className = 'w-full bg-gray-900 border border-gray-600 rounded p-2 text-white text-sm outline-none focus:border-blue-500';
        nitrousSelect.innerHTML = `
            <option value="">${T('stock_none', 'Stock / None')}</option>
            <option value="dry">${T('dry_shot_system', 'Dry Shot System')}</option>
            <option value="wet">${T('wet_shot_system', 'Wet Shot System')}</option>
        `;
        nitrousSelect.addEventListener('change', applyVehicleMods);
        nitrousDiv.appendChild(nitrousSelect);
    } else {
        nitrousDiv.innerHTML += `
            <select class="w-full bg-gray-900 border border-gray-600 rounded p-2 text-white text-sm opacity-40 cursor-not-allowed" disabled>
                <option>${T('stock_none', 'Stock / None')}</option>
            </select>
            <a href="${window.shopModsUrl || 'https://alphaxpr.com/collections/tunersim-mods'}" target="_blank"
               class="mt-1.5 block text-[10px] font-bold text-yellow-400 hover:text-yellow-300">${T('buy_nitrous_kit', 'Buy Nitrous Kit!')}</a>
        `;
    }
    rightDiv.appendChild(nitrousDiv);

    container.appendChild(rightDiv);

    // Column 3: Mod Settings
    const settingsDiv = document.createElement('div');
    settingsDiv.className = 'border-l border-gray-700 pl-6';
    settingsDiv.innerHTML = `<h4 class="text-xs text-blue-400 font-bold mb-3 uppercase tracking-widest">${T('mod_settings', 'Mod Settings')}</h4>`;

    // Turbo Settings
    const turboSet = document.createElement('div');
    turboSet.id = 'mod-settings-turbo';
    turboSet.className = 'mb-4 pointer-events-none transition-opacity';
    turboSet.style.opacity = '0.3';
    turboSet.innerHTML = `
        <label class="block text-[10px] text-gray-500 mb-1 font-bold uppercase tracking-widest">${T('mod_turbocharger', 'Turbocharger')}</label>
        <div class="bg-gray-900/50 p-2 rounded border border-gray-700">
            <label id="lbl-wg-spring" class="block text-[10px] text-gray-400 mb-1">${T('wastegate_spring_psi', 'Wastegate Spring (psi)')}</label>
            <input type="number" id="inp-wg-spring" step="0.5" value="7.0"
                class="w-full bg-gray-900 border border-gray-600 rounded p-1.5 text-white text-xs outline-none focus:border-blue-500"
                onchange="triggerTurboTableRescale()">
            <p class="text-[9px] text-gray-600 mt-1 italic">${T('map_axes_scale_help', 'MAP axes scale to 2&times; spring pressure.')}</p>
            <div class="mt-2 border-t border-gray-800 pt-2">
                <label class="flex items-center gap-2 text-[10px] text-gray-400 mb-1 cursor-pointer">
                    <input type="checkbox" id="inp-cl-boost-enabled"
                           class="rounded bg-gray-900 border-gray-600 text-blue-500 focus:ring-blue-500"
                           onchange="updateIdleSettings()">
                    ${T('closed_loop_boost', 'Closed-Loop Boost')}
                </label>
                <div id="cl-boost-settings-block" class="hidden flex flex-col gap-1 mt-1">
                    <div class="grid grid-cols-3 gap-1">
                        <div><label class="block text-[9px] text-gray-500">Kp</label>
                            <input type="number" id="inp-cl-boost-kp" step="0.05" value="0.8"
                                   class="w-full bg-gray-900 border border-gray-600 rounded p-1 text-white text-xs outline-none focus:border-blue-500"
                                   onchange="updateIdleSettings()"></div>
                        <div><label class="block text-[9px] text-gray-500">Ki</label>
                            <input type="number" id="inp-cl-boost-ki" step="0.01" value="0.15"
                                   class="w-full bg-gray-900 border border-gray-600 rounded p-1 text-white text-xs outline-none focus:border-blue-500"
                                   onchange="updateIdleSettings()"></div>
                        <div><label class="block text-[9px] text-gray-500">Kd</label>
                            <input type="number" id="inp-cl-boost-kd" step="0.01" value="0.05"
                                   class="w-full bg-gray-900 border border-gray-600 rounded p-1 text-white text-xs outline-none focus:border-blue-500"
                                   onchange="updateIdleSettings()"></div>
                    </div>
                </div>
            </div>
        </div>
    `;
    settingsDiv.appendChild(turboSet);

    // Supercharger Settings
    const scSet = document.createElement('div');
    scSet.id = 'mod-settings-sc';
    scSet.className = 'mb-4 pointer-events-none transition-opacity';
    scSet.style.opacity = '0.3';
    scSet.innerHTML = `
        <label class="block text-[10px] text-gray-500 mb-1 font-bold uppercase tracking-widest">${T('mod_supercharger', 'Supercharger')}</label>
        <div class="bg-gray-900/50 p-2 rounded border border-gray-700 flex flex-col gap-2">
            <div>
                <label class="block text-[10px] text-gray-400 mb-1">${T('pulley_ratio', 'Pulley Ratio (SC/Eng)')}</label>
                <input type="number" id="inp-sc-pulley" step="0.05" min="1.0" max="10.0" value="2.5" 
                    class="w-full bg-gray-900 border border-gray-600 rounded p-1.5 text-white text-xs outline-none focus:border-blue-500"
                    onchange="updateIdleSettings()">
            </div>
            <div>
                <label class="block text-[10px] text-gray-400 mb-1">${T('bypass_threshold', 'Bypass Threshold (%)')}</label>
                <input type="number" id="inp-sc-bypass" step="1" min="0" max="100" value="15" 
                    class="w-full bg-gray-900 border border-gray-600 rounded p-1.5 text-white text-xs outline-none focus:border-blue-500"
                    onchange="updateIdleSettings()">
            </div>
            <div class="flex justify-between items-center text-[10px] text-gray-500 border-t border-gray-800 pt-1">
                <span>${T('max_sc_rpm', 'Max SC RPM:')}</span>
                <span id="sc-rpm-display" class="text-orange-400 font-bold font-mono">--</span>
            </div>
        </div>
    `;
    settingsDiv.appendChild(scSet);

    const applyBtn = document.createElement('button');
    applyBtn.type = 'button';
    applyBtn.textContent = T('apply_settings', 'Apply Settings');
    applyBtn.className = 'mt-2 w-full bg-blue-600 hover:bg-blue-500 py-2 rounded font-bold transition-colors setting-apply-btn text-sm';
    applyBtn.onclick = () => updateIdleSettings();
    settingsDiv.appendChild(applyBtn);

    container.appendChild(settingsDiv);
    
    if (modsToForceCheck) {
        modsToForceCheck.forEach(m => {
            const cat = m.mod_type || 'Injector';
            const select = document.getElementById(`mod-select-${cat.toLowerCase()}`);
            if (select) {
                sampleModsDb[m.mod_id] = m;
                if (!select.querySelector(`option[value="${m.mod_id}"]`)) {
                    const opt = document.createElement('option');
                    opt.value = m.mod_id;
                    opt.text = m.display_name;
                    select.insertBefore(opt, select.lastElementChild);
                }
                select.value = m.mod_id;
            }
        });
    }

    // Restore previously selected mods (modsToForceCheck overrides its own category)
    const forcedCats = new Set((modsToForceCheck || []).map(m => m.mod_type || 'Other'));
    allCats.forEach(cat => {
        if (forcedCats.has(cat)) return;
        const prev = prevSelections[cat];
        if (!prev) return;
        const sel = document.getElementById(`mod-select-${cat.toLowerCase()}`);
        if (sel && sel.querySelector(`option[value="${prev}"]`)) sel.value = prev;
    });

    // Restore nitrous upgrade dropdown from ECU state (set before this call during tune import)
    const nitrousSel = document.getElementById('mod-select-nitrous');
    if (nitrousSel && ecu) {
        nitrousSel.value = ecu.nitrousEnabled ? (ecu.nitrousShotType || 'dry') : prevNitrous;
    }

    // Restore mod settings inputs/labels from ECU (DOM rebuild resets them to hardcoded defaults)
    const _ruIsImp = typeof useMetric !== 'undefined' && !useMetric;
    const wgSpringEl = document.getElementById('inp-wg-spring');
    if (wgSpringEl && ecu) {
        wgSpringEl.value = _ruIsImp ? parseFloat((ecu.wgSpringKpa * 0.14504).toFixed(1)) : ecu.wgSpringKpa;
    }
    const wgSpringLbl = document.getElementById('lbl-wg-spring');
    if (wgSpringLbl) wgSpringLbl.textContent = _ruIsImp ? T('wastegate_spring_psi', 'Wastegate Spring (psi)') : T('wastegate_spring_kpa', 'Wastegate Spring (kPa)');

    // Restore twin turbo checkbox and visibility after DOM rebuild
    const turboSelRestore = document.getElementById('mod-select-turbocharger');
    const twinOfferDiv = document.getElementById('twin-turbo-offer');
    if (twinOfferDiv && turboSelRestore) {
        const tv = turboSelRestore.value;
        twinOfferDiv.classList.toggle('hidden', !tv || tv.includes('.xsim-mod'));
    }
    const chkTt = document.getElementById('chk-twin-turbo');
    if (chkTt) chkTt.checked = twinTurboEnabled;

    applyVehicleMods();
}

function updateWgDutyNavState(hasTurbo) {
    const wrapper = document.getElementById('wg-duty-nav-wrapper');
    if (!wrapper) return;
    const btn = wrapper.querySelector('button');
    const tip = wrapper.querySelector('.adv-tooltip span:last-child');
    const locked = !advancedMode || !hasTurbo;
    wrapper.classList.toggle('adv-locked', locked);
    if (btn) btn.disabled = locked;
    if (tip) {
        tip.textContent = advancedMode ? 'Turbocharger required' : 'Advanced users only!';
    }
}

function updateClBoostNavState(hasTurbo) {
    const wrapper = document.getElementById('cl-boost-nav-wrapper');
    if (!wrapper) return;
    const btn = wrapper.querySelector('button');
    const tip = wrapper.querySelector('.adv-tooltip span:last-child');
    const locked = !advancedMode || !hasTurbo;
    wrapper.classList.toggle('adv-locked', locked);
    if (btn) btn.disabled = locked;
    if (tip) tip.textContent = advancedMode ? 'Turbocharger required' : 'Advanced users only!';
}

function updateClBoostUI() {
    const el = document.getElementById('cl-boost-settings-block');
    if (el) el.classList.toggle('hidden', !ecu.clBoostEnabled);
}

function applyVehicleMods() {
    const activeMods = [];
    const categories = ['Intake', 'Intake Manifold', 'Exhaust Manifold', 'Exhaust', 'Port and Polish', 'Pistons', 'Rods', 'Clutch', 'Turbocharger', 'Supercharger', 'Camshaft', 'Cooling', 'FuelPump', 'Injector'];

    let hasTurbo = false;
    let hasSC = false;
    let scProfile = null;
    categories.forEach(cat => {
        const select = document.getElementById(`mod-select-${cat.toLowerCase()}`);
        if (select) {
            const val = select.value;
            if (val && !val.includes('.xsim-mod') && val !== 'custom') {
                if (sampleModsDb[val]) {
                    activeMods.push(sampleModsDb[val]);
                    if (sampleModsDb[val].mod_type === "Turbocharger") hasTurbo = true;
                    if (sampleModsDb[val].mod_type === "Supercharger") {
                        hasSC = true;
                        scProfile = sampleModsDb[val].type_specific_parameters;
                    }
                }
            }
        }
    });
    _currentHasTurbo = hasTurbo;

    // Intercooler compatibility check — auto-deselect if type doesn't match active forced induction
    const coolingMod = activeMods.find(m => m.mod_type === "Cooling");
    if (coolingMod && coolingMod.type_specific_parameters?.compatible_with) {
        const compat = coolingMod.type_specific_parameters.compatible_with;
        // Determine which FI types are active
        const fiTypes = [];
        if (hasTurbo) fiTypes.push("Turbocharger");
        if (hasSC && scProfile) {
            const st = scProfile.sc_type;
            if (st === 'roots') fiTypes.push("Roots");
            else if (st === 'twin-screw') fiTypes.push("Twin-Screw");
            else if (st === 'centrifugal') fiTypes.push("Centrifugal");
        }
        const isCompatible = fiTypes.length === 0 || fiTypes.some(t => compat.includes(t));
        if (!isCompatible) {
            const coolingSelect = document.getElementById('mod-select-cooling');
            if (coolingSelect) coolingSelect.value = '';
            activeMods.splice(activeMods.indexOf(coolingMod), 1);
            alert(`Intercooler removed: "${coolingMod.display_name}" is not compatible with the selected supercharger type.\n\nRoots/Twin-Screw setups require an air-to-water intercooler; centrifugal/turbos use a front-mount.`);
        }
    }

    // Nitrous hardware selection (not in sampleModsDb — handled separately)
    const nitrousHardwareSelect = document.getElementById('mod-select-nitrous');
    const nitrousHardwareVal = nitrousHardwareSelect ? nitrousHardwareSelect.value : '';
    if (ecu) {
        ecu.nitrousEnabled = nitrousHardwareVal !== '';
        ecu.nitrousShotType = nitrousHardwareVal || 'dry';
        if (typeof updateNitrousUI === 'function') updateNitrousUI();
    }

    const boostContents = [
        document.getElementById('tab-wg-duty'),
        document.getElementById('boost-chart-wrapper'),
        document.getElementById('dyno-stat-boost-div'),
        document.getElementById('flex-boost-block')
    ];

    // Toggle Mod Settings Column subsections
    const turboSettings = document.getElementById('mod-settings-turbo');
    if (turboSettings) {
        turboSettings.style.opacity = hasTurbo ? '' : '0.3';
        turboSettings.classList.toggle('pointer-events-none', !hasTurbo);
    }
    const scSettings = document.getElementById('mod-settings-sc');
    if (scSettings) {
        scSettings.style.opacity = hasSC ? '' : '0.3';
        scSettings.classList.toggle('pointer-events-none', !hasSC);
        // Update computed SC RPM display
        const redlineRpm = ecu ? (ecu.revLimit || 7200) : 7200;
        const pulleyRatio = ecu ? (ecu.scPulleyRatio || 2.5) : 2.5;
        const scRpmAtRedline = document.getElementById('sc-rpm-display');
        if (scRpmAtRedline) scRpmAtRedline.textContent = Math.round(redlineRpm * pulleyRatio).toLocaleString() + ' RPM';
    }

    updateWgDutyNavState(hasTurbo);
    updateClBoostNavState(hasTurbo);

    if (hasTurbo || hasSC) {
        boostContents.forEach(el => { if(el) el.classList.remove('hidden'); });
    } else {
        boostContents.forEach(el => { if(el) el.classList.add('hidden'); });

        const currentTab = document.querySelector('.tab-content:not(.hidden)');
        if (currentTab && (currentTab.id === 'tab-wg-duty' || currentTab.id === 'tab-cl-boost')) {
            switchTab('tab-upgrades', document.querySelector('button[onclick*="tab-upgrades"]'));
        }
    }

    console.log('[applyVehicleMods] activeMods:', activeMods.map(m => `${m.mod_id} (${m.mod_type})`), '| twinTurbo:', twinTurboEnabled);
    if (physics) physics.loadMods(activeMods, twinTurboEnabled);

    // Exhaust mods deepen the audio tone proportional to their peak torque multiplier
    if (audio) {
        const MAX_EXHAUST_MULT = 1.15;
        let peakMult = 1.0;
        activeMods.forEach(mod => {
            if (mod.mod_type !== 'Exhaust' && mod.mod_type !== 'Exhaust Manifold') return;
            Object.values(mod.rpm_modifiers || {}).forEach(pt => {
                if ((pt.torque_multiplier || 1.0) > peakMult) peakMult = pt.torque_multiplier;
            });
        });
        const depthFactor = Math.min(1.0, Math.max(0, (peakMult - 1.0) / (MAX_EXHAUST_MULT - 1.0)));
        audio.setExhaustDepth(depthFactor);
    }
}

function showModToast(message, isError = false) {
    const t = document.createElement('div');
    t.textContent = message;
    const bg = isError ? '#ef4444' : '#22c55e';
    t.style.cssText = `position:fixed;top:20px;left:50%;transform:translateX(-50%);background:${bg};color:#fff;font-weight:700;font-size:0.85rem;padding:10px 22px;border-radius:8px;box-shadow:0 4px 24px rgba(0,0,0,.4);z-index:9999;pointer-events:none;transition:opacity .4s;white-space:nowrap;max-width:90vw;text-align:center;`;
    document.body.appendChild(t);
    setTimeout(() => { t.style.opacity = '0'; }, 3000);
    setTimeout(() => t.remove(), 3500);
}

async function fetchMod(url, type) {
    console.log('[fetchMod] Fetching:', url, '| type:', type);
    try {
        const response = await fetch(url);
        console.log('[fetchMod] HTTP status:', response.status, response.ok ? 'OK' : 'FAIL');
        if (!response.ok) throw new Error(`HTTP ${response.status} — file not found on CDN. Is it uploaded to Shopify Assets?`);
        const loadedMod = await response.json();
        console.log('[fetchMod] Parsed mod JSON:', JSON.stringify(loadedMod).slice(0, 200));

        sampleModsDb[loadedMod.mod_id] = loadedMod;
        console.log('[fetchMod] Stored in sampleModsDb under key:', loadedMod.mod_id);
        renderUpgradesUI();

        const dropdown = document.getElementById(`mod-select-${type.toLowerCase()}`);
        console.log('[fetchMod] Dropdown element:', dropdown ? `found (id=mod-select-${type.toLowerCase()})` : 'NOT FOUND');
        if (dropdown) {
            dropdown.value = loadedMod.mod_id;
            console.log('[fetchMod] Set dropdown.value to:', loadedMod.mod_id, '| actual value now:', dropdown.value);
            if (dropdown.value !== loadedMod.mod_id) {
                console.warn('[fetchMod] dropdown.value did not stick — option may be missing. Options:', [...dropdown.options].map(o => o.value));
            }
            applyVehicleMods();
        }
        showModToast(`Mod installed: ${loadedMod.display_name}`);
    } catch (err) {
        console.error('[fetchMod] FAILED:', err.message, err);
        showModToast(`Mod load failed: ${err.message}`, true);
        if (window.location.protocol === 'file:') {
            alert("Browser security blocks loading mods from the list when running locally (file://).\n\nPlease use the 'IMPORT MOD(S)' button above manually.");
        }
    }
}

// --- TRACE & LIVE FEEDBACK ---

let lastTraceIndices = {
    main: { r: -1, c: -1 },
    vvt: { r: -1, c: -1 },
    flex: { r: -1, c: -1, wgR: -1 },
    eth: { c: -1 },
    ect: { c: -1 },
    crk: { c: -1 },
    iat: { c: -1 },
    knk: { c: -1 },
    alt: { r: -1, c: -1 },
    volt: { c: -1 },
    wg: { r: -1, c: -1 },
    accel: { r: -1, c: -1 },
    accelMs: { r: -1, c: -1 },
    clBoost: { r: -1, c: -1 },
    boostGear: { c: -1 }
};
let currentlyTracedElements = [];

function closestIndex(sortedArr, value) {
    let lo = 0, hi = sortedArr.length - 1;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (sortedArr[mid] < value) lo = mid + 1;
        else hi = mid;
    }
    if (lo > 0 && Math.abs(sortedArr[lo - 1] - value) <= Math.abs(sortedArr[lo] - value)) return lo - 1;
    return lo;
}

function updateTrace(rpm, mapKpa, dTps = 0) {
    if (!ecu || rpm < 0 || mapKpa < 0) {
        if (currentlyTracedElements.length > 0) {
            currentlyTracedElements.forEach(el => el.classList.remove('cell-trace'));
            currentlyTracedElements = [];
            // Reset indices so it forces update when engine starts again
            for (let k in lastTraceIndices) { for (let axis in lastTraceIndices[k]) lastTraceIndices[k][axis] = -1; }
        }
        return;
    }

    const actualTpsEl = document.getElementById('data-tps');
    let actualTps = actualTpsEl ? parseFloat(actualTpsEl.innerText) : 0;

    // 1. Calculate best indices for all table types
    const bestC = closestIndex(ecu.rpmAxis, rpm);
    const bestR = closestIndex(ecu.mapAxis, mapKpa);

    const bestVvtC = closestIndex(ecu.vvtRpmAxis, rpm);
    const bestVvtR = closestIndex(ecu.vvtMapAxis, mapKpa);

    const bestFlexC   = closestIndex(ecu.flexRpmAxis, rpm);
    const bestFlexR   = closestIndex(ecu.flexMapAxis, mapKpa);
    const bestFlexWgR = closestIndex(ecu.flexTpsAxis, actualTps);

    const eth = ecu.ethanolContent || 0;
    const bestEthC = closestIndex(ecu.ethXAxis, eth);

    let bestEctIdx = -1;
    if (typeof currentCoolantC !== 'undefined' && currentCoolantC !== null && ecu.idleDutyCoolantAxis) {
        bestEctIdx = closestIndex(ecu.idleDutyCoolantAxis, currentCoolantC);
    }

    let bestCrkIdx = -1;
    if (typeof currentCoolantC !== 'undefined' && currentCoolantC !== null && ecu.crankingIdleDutyAxis) {
        bestCrkIdx = closestIndex(ecu.crankingIdleDutyAxis, currentCoolantC);
    }

    let bestIatC = -1;
    if (typeof currentIatC !== 'undefined' && ecu.iatVeAxis) {
        bestIatC = closestIndex(ecu.iatVeAxis, currentIatC);
    }

    let bestKnkC = -1;
    if (ecu.knockThresholdAxis) {
        bestKnkC = closestIndex(ecu.knockThresholdAxis, rpm);
    }

    let bestAltC = -1, bestAltR = -1;
    if (ecu.altVoltRpmAxis && ecu.altVoltLoadAxis) {
        bestAltC = closestIndex(ecu.altVoltRpmAxis, rpm);
        bestAltR = closestIndex(ecu.altVoltLoadAxis, mapKpa);
    }

    let bestVoltC = -1;
    if (typeof batteryVoltage !== 'undefined' && ecu.injDtAxis) {
        bestVoltC = closestIndex(ecu.injDtAxis, batteryVoltage);
    }

    const bestWgC = bestC;
    const bestWgR = closestIndex(ecu.tpsAxis, actualTps);

    const bestClBoostC = closestIndex(ecu.clBoostRpmAxis, rpm);
    const bestClBoostR = closestIndex(ecu.clBoostTpsAxis, actualTps);
    let bestBoostGearC = -1;
    if (typeof getCurrentGear === 'function') {
        bestBoostGearC = closestIndex(ecu.boostGearAxis, getCurrentGear());
    }

    let bestAccelC = -1, bestAccelR = -1;
    if (ecu.accelMapTpsAxis && ecu.accelMapRpmAxis) {
        bestAccelC = closestIndex(ecu.accelMapRpmAxis, rpm);
        bestAccelR = closestIndex(ecu.accelMapTpsAxis, actualTps);
    }

    let bestAccelMsC = -1, bestAccelMsR = -1;
    if (ecu.accelMsDtpsAxis && ecu.accelMsRpmAxis) {
        bestAccelMsC = closestIndex(ecu.accelMsRpmAxis, rpm);
        bestAccelMsR = closestIndex(ecu.accelMsDtpsAxis, dTps);
    }

    // 2. Check if anything actually changed before touching the DOM
    const changed =
        lastTraceIndices.main.r !== bestR || lastTraceIndices.main.c !== bestC ||
        lastTraceIndices.vvt.r  !== bestVvtR || lastTraceIndices.vvt.c  !== bestVvtC ||
        lastTraceIndices.flex.r !== bestFlexR || lastTraceIndices.flex.c !== bestFlexC || lastTraceIndices.flex.wgR !== bestFlexWgR ||
        lastTraceIndices.eth.c  !== bestEthC || lastTraceIndices.ect.c  !== bestEctIdx ||
        lastTraceIndices.crk.c  !== bestCrkIdx || lastTraceIndices.iat.c  !== bestIatC ||
        lastTraceIndices.knk.c  !== bestKnkC || lastTraceIndices.alt.r  !== bestAltR || lastTraceIndices.alt.c !== bestAltC ||
        lastTraceIndices.volt.c !== bestVoltC || lastTraceIndices.wg.r   !== bestWgR || lastTraceIndices.wg.c !== bestWgC ||
        lastTraceIndices.accel.r !== bestAccelR || lastTraceIndices.accel.c !== bestAccelC ||
        lastTraceIndices.accelMs.r !== bestAccelMsR || lastTraceIndices.accelMs.c !== bestAccelMsC ||
        lastTraceIndices.clBoost.r !== bestClBoostR || lastTraceIndices.clBoost.c !== bestClBoostC ||
        lastTraceIndices.boostGear.c !== bestBoostGearC;

    if (!changed) return;

    // 3. Update the DOM
    currentlyTracedElements.forEach(el => el.classList.remove('cell-trace'));
    currentlyTracedElements = [];

    // Main Tables
    document.querySelectorAll(`#container-ve-table td[data-r="${bestR}"][data-c="${bestC}"], #container-afr-table td[data-r="${bestR}"][data-c="${bestC}"], #container-timing-table td[data-r="${bestR}"][data-c="${bestC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });
    
    // VVT
    document.querySelectorAll(`#container-vvt-intake-table td[data-r="${bestVvtR}"][data-c="${bestVvtC}"], #container-vvt-exhaust-table td[data-r="${bestVvtR}"][data-c="${bestVvtC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });

    // Flex
    document.querySelectorAll(`#container-flex-ve-table td[data-r="${bestFlexR}"][data-c="${bestFlexC}"], #container-flex-afr-table td[data-r="${bestFlexR}"][data-c="${bestFlexC}"], #container-flex-timing-table td[data-r="${bestFlexR}"][data-c="${bestFlexC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });
    document.querySelectorAll(`#container-flex-boost-table td[data-r="${bestFlexWgR}"][data-c="${bestFlexC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });
    document.querySelectorAll(`#container-flex-ve-curve td[data-c="${bestEthC}"], #container-flex-afr-curve td[data-c="${bestEthC}"], #container-flex-timing-curve td[data-c="${bestEthC}"], #container-flex-boost-curve td[data-c="${bestEthC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });

    // ECT
    if (bestEctIdx !== -1) {
        document.querySelectorAll(`#container-idle-duty-curve td[data-c="${bestEctIdx}"], #container-idle-rpm-curve td[data-c="${bestEctIdx}"], #container-ect-fuel-curve td[data-c="${bestEctIdx}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });
    }
    if (bestCrkIdx !== -1) {
        document.querySelectorAll(`#container-cranking-duty-curve td[data-c="${bestCrkIdx}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });
        document.querySelectorAll(`#container-post-crank-enrich-curve td[data-c="${bestCrkIdx}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });
    }

    // IAT
    if (bestIatC !== -1) {
        document.querySelectorAll(`#container-iat-ve-curve td[data-c="${bestIatC}"], #container-iat-timing-curve td[data-c="${bestIatC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });
    }

    // Knock
    if (bestKnkC !== -1) {
        document.querySelectorAll(`#container-knock-threshold-curve td[data-c="${bestKnkC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });
    }

    // Alt
    if (bestAltR !== -1 && bestAltC !== -1) {
        document.querySelectorAll(`#container-alt-volt-table td[data-r="${bestAltR}"][data-c="${bestAltC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });
    }

    // Voltage
    if (bestVoltC !== -1) {
        document.querySelectorAll(`#container-inj-dt-curve td[data-c="${bestVoltC}"], #container-coil-dwell-curve td[data-c="${bestVoltC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });
    }

    // Wastegate
    document.querySelectorAll(`#container-wg-duty-table td[data-r="${bestWgR}"][data-c="${bestWgC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });

    // Accel MAP
    if (bestAccelR !== -1 && bestAccelC !== -1) {
        document.querySelectorAll(`#container-accel-map-table td[data-r="${bestAccelR}"][data-c="${bestAccelC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });
    }

    // Accel MS Adder
    if (bestAccelMsR !== -1 && bestAccelMsC !== -1) {
        document.querySelectorAll(`#container-accel-ms-table td[data-r="${bestAccelMsR}"][data-c="${bestAccelMsC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });
    }

    // CL Boost Target
    document.querySelectorAll(`#container-cl-boost-target-table td[data-r="${bestClBoostR}"][data-c="${bestClBoostC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });

    // Boost Gear Compensation
    if (bestBoostGearC !== -1) {
        document.querySelectorAll(`#container-boost-duty-gear-comp-curve td[data-c="${bestBoostGearC}"], #container-boost-target-gear-comp-curve td[data-c="${bestBoostGearC}"]`).forEach(el => { el.classList.add('cell-trace'); currentlyTracedElements.push(el); });
    }

    // 4. Save state
    lastTraceIndices.main.r = bestR; lastTraceIndices.main.c = bestC;
    lastTraceIndices.vvt.r = bestVvtR; lastTraceIndices.vvt.c = bestVvtC;
    lastTraceIndices.flex.r = bestFlexR; lastTraceIndices.flex.c = bestFlexC; lastTraceIndices.flex.wgR = bestFlexWgR;
    lastTraceIndices.eth.c = bestEthC; lastTraceIndices.ect.c = bestEctIdx;
    lastTraceIndices.crk.c = bestCrkIdx; lastTraceIndices.iat.c = bestIatC;
    lastTraceIndices.knk.c = bestKnkC; lastTraceIndices.alt.r = bestAltR; lastTraceIndices.alt.c = bestAltC;
    lastTraceIndices.volt.c = bestVoltC; lastTraceIndices.wg.r = bestWgR; lastTraceIndices.wg.c = bestWgC;
    lastTraceIndices.accel.r = bestAccelR; lastTraceIndices.accel.c = bestAccelC;
    lastTraceIndices.accelMs.r = bestAccelMsR; lastTraceIndices.accelMs.c = bestAccelMsC;
    lastTraceIndices.clBoost.r = bestClBoostR; lastTraceIndices.clBoost.c = bestClBoostC;
    lastTraceIndices.boostGear.c = bestBoostGearC;
}

// --- TUNE EXPORT/IMPORT ---

function exportTune(event) {
    try {
        if (event && event.shiftKey) {
            exportTuneJson();
        } else {
            exportTuneBinary();
        }
        window.tuneExported = true;
    } catch (err) {
        console.error("Export failed:", err);
        alert("Failed to export tune: " + err.message);
    }
}

function exportTuneBinary() {
    if (!ECU_MEMORY_MAP) { ensureMemoryMap(() => exportTuneBinary()); return; }
    const buf = new ArrayBuffer(65536);
    const view = new DataView(buf);
    new Uint8Array(buf).fill(0xFF);
    // Magic header "XSIM"
    [0x58, 0x53, 0x49, 0x4D].forEach((b, i) => view.setUint8(i, b));

    for (const field of ECU_MEMORY_MAP) {
        const { name, offset, type, scale, dimensions } = field;
        const ecuProp = ECU_FIELD_MAP[name] ?? name;
        let raw = ecu[ecuProp];
        if (raw === undefined) continue;
        if (ECU_ENUM_ENCODE[name] !== undefined) raw = ECU_ENUM_ENCODE[name][raw] ?? 0;
        if (typeof raw === 'boolean') raw = raw ? 1 : 0;

        // Build write order: 2D tables use column-major (X/RPM outer, MAP inner)
        // so TunerPro XDF reads them correctly. 1D and scalars write as-is.
        let writeVals;
        if (dimensions.length === 2) {
            const [rows, cols] = dimensions;
            writeVals = [];
            for (let c = 0; c < cols; c++)
                for (let r = 0; r < rows; r++)
                    writeVals.push(raw[r][c]);
        } else {
            writeVals = Array.isArray(raw) ? raw.flat() : [raw];
        }

        let off = offset;
        for (const val of writeVals) {
            const scaled = Math.round(val / scale);
            if      (type === 'uint8'  || type === 'bool') { view.setUint8(off,  scaled & 0xFF);        off += 1; }
            else if (type === 'int8')                      { view.setInt8(off,   scaled);                off += 1; }
            else if (type === 'uint16')                    { view.setUint16(off, scaled & 0xFFFF, true); off += 2; }
            else if (type === 'int16')                     { view.setInt16(off,  scaled,          true); off += 2; }
            else if (type === 'float32')                   { view.setFloat32(off, val,            true); off += 4; }
        }
    }

    const engineId = sampleEngine?.global_specs?.engine_id || "Custom";
    const blob = new Blob([buf], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `AlphaX_Tune_${engineId.replace(/\s+/g, '_')}.bin`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

function exportTuneJson() {
    const engineId = sampleEngine?.global_specs?.engine_id || "Custom";
    const engineLayout = sampleEngine?.global_specs?.displacement_cc + "cc " + sampleEngine?.global_specs?.engine_layout + sampleEngine?.global_specs?.cylinders;
    const fileName = `AlphaX_Tune_${engineId.replace(/\s+/g, '_')}.json`;
    const tuneData = {
            metadata: { engine_id: engineId, engine: engineLayout, export_date: new Date().toISOString() },
            active_mods: physics?.activeMods || [],
            tables: {
                ve: ecu.veTable, target_afr: ecu.targetAfrTable, ignition: ecu.timingTable,
                vvt_intake: ecu.vvtIntakeTable, vvt_exhaust: ecu.vvtExhaustTable, wg_duty: ecu.wgDutyTable,
                flex_ve: ecu.flexVeTable, flex_afr: ecu.flexAfrTable, flex_timing: ecu.flexTimingTable, flex_boost: ecu.flexBoostTable,
                flex_ve_curve: ecu.flexVeBlendCurve, flex_afr_curve: ecu.flexAfrBlendCurve, flex_timing_curve: ecu.flexTimingBlendCurve, flex_boost_curve: ecu.flexBoostBlendCurve,
                rpm_axis: ecu.rpmAxis, map_axis: ecu.mapAxis,
                vvt_rpm_axis: ecu.vvtRpmAxis, vvt_map_axis: ecu.vvtMapAxis,
                wg_rpm_axis: ecu.wgRpmAxis, tps_axis: ecu.tpsAxis,
                flex_rpm_axis: ecu.flexRpmAxis, flex_ethanol_axis: ecu.flexEthanolAxis,
                idle_duty_curve: ecu.idleDutyCurve, idle_duty_coolant_axis: ecu.idleDutyCoolantAxis,
                ect_fuel_curve: ecu.ectFuelCurve, ect_fuel_axis: ecu.ectFuelAxis,
                alt_volt_table: ecu.altVoltTable, alt_volt_rpm_axis: ecu.altVoltRpmAxis, alt_volt_load_axis: ecu.altVoltLoadAxis,
                cl_boost_target: ecu.clBoostTargetTable, cl_boost_rpm_axis: ecu.clBoostRpmAxis, cl_boost_tps_axis: ecu.clBoostTpsAxis,
                boost_gear_axis: ecu.boostGearAxis,
                boost_duty_gear_comp: ecu.boostDutyGearComp,
                boost_target_gear_comp: ecu.boostTargetGearComp,
                flex_map_axis: ecu.flexMapAxis, flex_tps_axis: ecu.flexTpsAxis,
                iat_timing_axis: ecu.iatTimingAxis, iat_timing_curve: ecu.iatTimingCurve,
                iat_ve_axis: ecu.iatVeAxis, iat_ve_curve: ecu.iatVeCurve,
                knock_threshold_axis: ecu.knockThresholdAxis, knock_threshold_curve: ecu.knockThresholdCurve,
                accel_ms_dtps_axis: ecu.accelMsDtpsAxis, accel_ms_rpm_axis: ecu.accelMsRpmAxis, accel_ms_table: ecu.accelMsTable,
                accel_map_tps_axis: ecu.accelMapTpsAxis, accel_map_rpm_axis: ecu.accelMapRpmAxis, accel_map_table: ecu.accelMapTable,
                inj_dt_axis: ecu.injDtAxis, inj_dt_curve: ecu.injDtCurve,
                coil_dwell_axis: ecu.coilDwellAxis, coil_dwell_curve: ecu.coilDwellCurve
            },
            settings: {
                maxIdleTpsPct: ecu.maxIdleTpsPct, idleRpmUpperLimit: ecu.idleRpmUpperLimit,
                idleTargetRpmAxis: ecu.idleTargetRpmAxis, idleTargetRpmCurve: ecu.idleTargetRpmCurve,
                revLimit: ecu.revLimit,
                revLimitHysteresis: ecu.revLimitHysteresis, revLimitFuelCut: ecu.revLimitFuelCut, revLimitSparkCut: ecu.revLimitSparkCut,
                injectorSizeCc: ecu.injectorSizeCc, injectorDeadTimeMs: ecu.injectorDeadTimeMs, fuelOctane: ecu.fuelOctane,
                idleIgnEnabled: ecu.idleIgnEnabled, idleIgnP: ecu.idleIgnP, idleIgnI: ecu.idleIgnI, idleIgnD: ecu.idleIgnD,
                decelCutEnabled: ecu.decelCutEnabled, decelCutDelay: ecu.decelCutDelay,
                closedLoopEnabled: ecu.closedLoopEnabled, clDeadband: ecu.clDeadband,
                clIdleTimeConstant: ecu.clIdleTimeConstant, clIdleMin: ecu.clIdleMin, clIdleMax: ecu.clIdleMax,
                clRunningTimeConstant: ecu.clRunningTimeConstant, clRunningMin: ecu.clRunningMin, clRunningMax: ecu.clRunningMax,
                clWotTimeConstant: ecu.clWotTimeConstant, clWotMin: ecu.clWotMin, clWotMax: ecu.clWotMax,
                o2DelayEnabled: ecu.o2DelayEnabled,
                crankingVe: ecu.crankingVe, crankingDecayTime: ecu.crankingDecayTime,
                maxCrankingRpm: ecu.maxCrankingRpm, starterCrankTime: ecu.starterCrankTime,
                crankingIdleDutyAxis: ecu.crankingIdleDutyAxis, crankingIdleDutyCurve: ecu.crankingIdleDutyCurve,
                postCrankEnrichAxis: ecu.postCrankEnrichAxis, postCrankEnrichCurve: ecu.postCrankEnrichCurve,
                postCrankDecayCycles: ecu.postCrankDecayCycles,
                camSwitchRpm: ecu.camSwitchRpm, camSwitchTps: ecu.camSwitchTps, vvlFuelAdder: ecu.vvlFuelAdder, vvlTimingAdder: ecu.vvlTimingAdder,
                vvtPhaserEnabled: ecu.vvtPhaserEnabled, vvtKp: ecu.vvtKp, vvtKi: ecu.vvtKi, vvtKd: ecu.vvtKd,
                wgSpringKpa: ecu.wgSpringKpa, boostCutKpa: ecu.boostCutKpa, boostCutHysteresisKpa: ecu.boostCutHysteresisKpa,
                clBoostEnabled: ecu.clBoostEnabled, clBoostKp: ecu.clBoostKp, clBoostKi: ecu.clBoostKi, clBoostKd: ecu.clBoostKd,
                scPulleyRatio: ecu.scPulleyRatio, scBypassThreshold: ecu.scBypassThreshold,
                triggerAngleOffset: ecu.triggerAngleOffset, fixedTimingAngle: ecu.fixedTimingAngle,
                ignitionMode: ecu.ignitionMode, sparkPlugGapIn: ecu.sparkPlugGapIn,
                accelMode: ecu.accelMode, accelTime: ecu.accelTime, accelDecayRate: ecu.accelDecayRate,
                knockEnabled: ecu.knockEnabled, knockAttackDeg: ecu.knockAttackDeg, knockMaxRetardDeg: ecu.knockMaxRetardDeg,
                knockRecoveryDegPerRev: ecu.knockRecoveryDegPerRev, knockMinTps: ecu.knockMinTps, knockMinMap: ecu.knockMinMap,
                knockCenterFreq: ecu.knockCenterFreq, knockCalibrated: ecu.knockCalibrated,
                smartAltEnabled: ecu.smartAltEnabled, smartAltP: ecu.smartAltP, smartAltI: ecu.smartAltI, smartAltD: ecu.smartAltD,
                lcEnabled: ecu.lcEnabled, lcRpm: ecu.lcRpm, lcHysteresis: ecu.lcHysteresis, lcWindowRpm: ecu.lcWindowRpm,
                lcCutType: ecu.lcCutType, lcTimingAbsolute: ecu.lcTimingAbsolute, lcWindowStartTiming: ecu.lcWindowStartTiming, lcFuelAdder: ecu.lcFuelAdder,
                nitrousEnabled: ecu.nitrousEnabled, nitrousShotType: ecu.nitrousShotType,
                nitrousJetSize: ecu.nitrousJetSize, nitrousActivationMode: ecu.nitrousActivationMode,
                nitrousFuelJetSize: ecu.nitrousFuelJetSize, nitrousMinTps: ecu.nitrousMinTps,
                nitrousMinRpm: ecu.nitrousMinRpm, nitrousMaxRpm: ecu.nitrousMaxRpm,
                nitrousMaxMapKpa: ecu.nitrousMaxMapKpa, nitrousAfrCutLean: ecu.nitrousAfrCutLean,
                nitrousVeCompPct: ecu.nitrousVeCompPct, nitrousTimingRetard: ecu.nitrousTimingRetard,
                fuelPressureModelEnabled: ecu.fuelPressureModelEnabled,
                baseFuelPressureKpa: ecu.baseFuelPressureKpa,
                fuelSystemType: ecu.fuelSystemType,
                injectorReferencePressureKpa: ecu.injectorReferencePressureKpa
            }
        };
        
        const blob = new Blob([JSON.stringify(tuneData, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const dlAnchorElem = document.createElement('a');
        dlAnchorElem.setAttribute("href", url);
        dlAnchorElem.setAttribute("download", fileName);
        document.body.appendChild(dlAnchorElem);
        dlAnchorElem.click();
        document.body.removeChild(dlAnchorElem);
        URL.revokeObjectURL(url);
}

// --- TURBO TABLE RESCALE ---

function triggerTurboTableRescale() {
    // Axis auto-expansion removed; use the Axis Wizard instead.
    updateIdleSettings();
}

// --- TUNE IMPORT ---

function _round1dp(v) { return Math.round(v * 10) / 10; }
function _round2dp(v) { return Math.round(v * 100) / 100; }
function _roundTable(arr) { return arr.map(row => row.map(_round1dp)); }
function _roundCurve(arr) { return arr.map(_round1dp); }
function _roundCurve2dp(arr) { return arr.map(_round2dp); }

function importTuneJson(loadedTune, e) {
    try {

                if (loadedTune.metadata.engine_id !== sampleEngine.global_specs.engine_id) {
                    alert(`Tune mismatch! This tune is designed for the ${loadedTune.metadata.engine_id}, but you currently have the ${sampleEngine.global_specs.engine_id} loaded. Please swap your engine first!`);
                    e.target.value = '';
                    return;
                }

                if (loadedTune.tables) {
                    const t = loadedTune.tables;
                    if (t.ve) ecu.veTable = _roundTable(t.ve);
                    if (t.target_afr) ecu.targetAfrTable = _roundTable(t.target_afr);
                    if (t.ignition) ecu.timingTable = _roundTable(t.ignition);
                    if (t.vvt_intake) ecu.vvtIntakeTable = _roundTable(t.vvt_intake);
                    if (t.vvt_exhaust) ecu.vvtExhaustTable = _roundTable(t.vvt_exhaust);
                    if (t.wg_duty) ecu.wgDutyTable = _roundTable(t.wg_duty);
                    if (t.flex_ve) ecu.flexVeTable = _roundTable(t.flex_ve);
                    if (t.flex_afr) ecu.flexAfrTable = _roundTable(t.flex_afr);
                    if (t.flex_timing) ecu.flexTimingTable = _roundTable(t.flex_timing);
                    if (t.flex_boost) ecu.flexBoostTable = _roundTable(t.flex_boost);
                    if (t.flex_ve_curve) ecu.flexVeBlendCurve = _roundCurve(t.flex_ve_curve);
                    if (t.flex_afr_curve) ecu.flexAfrBlendCurve = _roundCurve(t.flex_afr_curve);
                    if (t.flex_timing_curve) ecu.flexTimingBlendCurve = _roundCurve(t.flex_timing_curve);
                    if (t.flex_boost_curve) ecu.flexBoostBlendCurve = _roundCurve(t.flex_boost_curve);
                    if (t.flex_rpm_axis) ecu.flexRpmAxis = t.flex_rpm_axis;
                    if (t.flex_ethanol_axis) ecu.flexEthanolAxis = t.flex_ethanol_axis;
                    if (t.idle_duty_curve) ecu.idleDutyCurve = _roundCurve(t.idle_duty_curve);
                    if (t.idle_duty_coolant_axis) ecu.idleDutyCoolantAxis = t.idle_duty_coolant_axis;
                    if (t.ect_fuel_curve) ecu.ectFuelCurve = _roundCurve2dp(t.ect_fuel_curve);
                    if (t.ect_fuel_axis) ecu.ectFuelAxis = t.ect_fuel_axis;
                    if (t.alt_volt_table) ecu.altVoltTable = _roundTable(t.alt_volt_table);
                    if (t.alt_volt_rpm_axis) ecu.altVoltRpmAxis = t.alt_volt_rpm_axis;
                    if (t.alt_volt_load_axis) ecu.altVoltLoadAxis = t.alt_volt_load_axis;
                    if (t.cl_boost_target) ecu.clBoostTargetTable = _roundTable(t.cl_boost_target);
                    if (t.cl_boost_rpm_axis) ecu.clBoostRpmAxis = t.cl_boost_rpm_axis;
                    if (t.cl_boost_tps_axis) ecu.clBoostTpsAxis = t.cl_boost_tps_axis;
                    if (t.boost_gear_axis) ecu.boostGearAxis = t.boost_gear_axis;
                    if (t.boost_duty_gear_comp) ecu.boostDutyGearComp = _roundCurve(t.boost_duty_gear_comp);
                    if (t.boost_target_gear_comp) ecu.boostTargetGearComp = _roundCurve(t.boost_target_gear_comp);
                    if (t.flex_map_axis) ecu.flexMapAxis = t.flex_map_axis;
                    if (t.flex_tps_axis) ecu.flexTpsAxis = t.flex_tps_axis;
                    if (t.iat_timing_axis) ecu.iatTimingAxis = t.iat_timing_axis;
                    if (t.iat_timing_curve) ecu.iatTimingCurve = _roundCurve(t.iat_timing_curve);
                    if (t.iat_ve_axis) ecu.iatVeAxis = t.iat_ve_axis;
                    if (t.iat_ve_curve) ecu.iatVeCurve = _roundCurve(t.iat_ve_curve);
                    if (t.knock_threshold_axis) ecu.knockThresholdAxis = t.knock_threshold_axis;
                    if (t.knock_threshold_curve) ecu.knockThresholdCurve = _roundCurve(t.knock_threshold_curve);
                    if (t.accel_ms_dtps_axis) ecu.accelMsDtpsAxis = t.accel_ms_dtps_axis;
                    if (t.accel_ms_rpm_axis) ecu.accelMsRpmAxis = t.accel_ms_rpm_axis;
                    if (t.accel_ms_table) ecu.accelMsTable = _roundTable(t.accel_ms_table);
                    if (t.accel_map_tps_axis) ecu.accelMapTpsAxis = t.accel_map_tps_axis;
                    if (t.accel_map_rpm_axis) ecu.accelMapRpmAxis = t.accel_map_rpm_axis;
                    if (t.accel_map_table) ecu.accelMapTable = _roundTable(t.accel_map_table);
                    if (t.inj_dt_axis) ecu.injDtAxis = t.inj_dt_axis;
                    if (t.inj_dt_curve) ecu.injDtCurve = _roundCurve2dp(t.inj_dt_curve);
                    if (t.coil_dwell_axis) ecu.coilDwellAxis = t.coil_dwell_axis;
                    if (t.coil_dwell_curve) ecu.coilDwellCurve = _roundCurve(t.coil_dwell_curve);
                    if (t.rpm_axis) ecu.rpmAxis = t.rpm_axis;
                    if (t.map_axis) ecu.mapAxis = t.map_axis;
                    if (t.vvt_rpm_axis) ecu.vvtRpmAxis = t.vvt_rpm_axis;
                    if (t.vvt_map_axis) ecu.vvtMapAxis = t.vvt_map_axis;
                    if (t.wg_rpm_axis) ecu.wgRpmAxis = t.wg_rpm_axis;
                    if (t.tps_axis) ecu.tpsAxis = t.tps_axis;
                }

                if (loadedTune.settings) {
                    const s = loadedTune.settings;
                    const elMaxIdleTps = document.getElementById('inp-max-idle-tps'); if (elMaxIdleTps) { ecu.maxIdleTpsPct = s.maxIdleTpsPct ?? 10; elMaxIdleTps.value = ecu.maxIdleTpsPct; }
                    const elIdleRpmLimit = document.getElementById('inp-idle-rpm-upper-limit'); if (elIdleRpmLimit) { ecu.idleRpmUpperLimit = s.idleRpmUpperLimit ?? 200; elIdleRpmLimit.value = ecu.idleRpmUpperLimit; }
                    if (s.idleTargetRpmAxis) ecu.idleTargetRpmAxis = s.idleTargetRpmAxis;
                    if (s.idleTargetRpmCurve) ecu.idleTargetRpmCurve = s.idleTargetRpmCurve;
                    const elRevLimit = document.getElementById('inp-rev-limit'); if (elRevLimit) elRevLimit.value = s.revLimit ?? 7200;
                    // idleDutyBase (legacy scalar) is no longer used; idle duty is now a curve
                    const elInjSize = document.getElementById('inp-inj-size'); if (elInjSize) elInjSize.value = s.injectorSizeCc ?? 240;
                    const elDecelCut = document.getElementById('inp-decel-cut'); if (elDecelCut) elDecelCut.checked = s.decelCutEnabled ?? false;
                    const elDecelDelay = document.getElementById('inp-decel-delay'); if (elDecelDelay) elDecelDelay.value = s.decelCutDelay ?? 1.5;
                    const elIdleIgn = document.getElementById('inp-idle-ign-en'); if (elIdleIgn) elIdleIgn.checked = s.idleIgnEnabled ?? false;
                    const elIdleP = document.getElementById('inp-idle-p'); if (elIdleP) elIdleP.value = s.idleIgnP ?? 0.015;
                    const elIdleI = document.getElementById('inp-idle-i'); if (elIdleI) elIdleI.value = s.idleIgnI ?? 0;
                    const elIdleD = document.getElementById('inp-idle-d'); if (elIdleD) elIdleD.value = s.idleIgnD ?? 0;
                    const elCrankVe = document.getElementById('inp-crank-ve'); if (elCrankVe) elCrankVe.value = s.crankingVe ?? 50;
                    const elCrankDecay = document.getElementById('inp-crank-decay'); if (elCrankDecay) elCrankDecay.value = s.crankingDecayTime ?? 3.0;
                    const elMaxCrankRpm = document.getElementById('inp-max-crank-rpm'); if (elMaxCrankRpm) elMaxCrankRpm.value = s.maxCrankingRpm ?? 300;
                    const elStarterCrankTime = document.getElementById('inp-starter-crank-time'); if (elStarterCrankTime) elStarterCrankTime.value = s.starterCrankTime ?? 2.0;
                    if (s.crankingIdleDutyAxis) ecu.crankingIdleDutyAxis = s.crankingIdleDutyAxis;
                    if (s.crankingIdleDutyCurve) ecu.crankingIdleDutyCurve = s.crankingIdleDutyCurve;
                    if (s.postCrankEnrichAxis) ecu.postCrankEnrichAxis = s.postCrankEnrichAxis;
                    if (s.postCrankEnrichCurve) ecu.postCrankEnrichCurve = s.postCrankEnrichCurve;
                    const elPcdc = document.getElementById('inp-post-crank-decay-cycles'); if (elPcdc) elPcdc.value = s.postCrankDecayCycles ?? 50;
                    const elCamSwitch = document.getElementById('inp-cam-switch'); if (elCamSwitch) elCamSwitch.value = s.camSwitchRpm ?? 5500;
                    const elCamSwitchTps = document.getElementById('inp-cam-switch-tps'); if (elCamSwitchTps) elCamSwitchTps.value = s.camSwitchTps ?? 20;
                    const elFuelOctane = document.getElementById('inp-fuel-octane'); if (elFuelOctane) elFuelOctane.value = s.fuelOctane ?? 91;
                    const elVvlFuel = document.getElementById('inp-vvl-fuel'); if (elVvlFuel) elVvlFuel.value = s.vvlFuelAdder ?? 0;
                    const elVvlTiming = document.getElementById('inp-vvl-timing'); if (elVvlTiming) elVvlTiming.value = s.vvlTimingAdder ?? 0;
                    if (s.vvtPhaserEnabled !== undefined) {
                        ecu.vvtPhaserEnabled = s.vvtPhaserEnabled;
                        const elPhaserEn = document.getElementById('inp-vvt-phaser-enable');
                        if (elPhaserEn) { elPhaserEn.checked = s.vvtPhaserEnabled; updateVvtPhaserUI(); }
                    }
                    if (s.vvtKp !== undefined) { ecu.vvtKp = s.vvtKp; const el = document.getElementById('inp-vvt-kp'); if (el) el.value = s.vvtKp; }
                    if (s.vvtKi !== undefined) { ecu.vvtKi = s.vvtKi; const el = document.getElementById('inp-vvt-ki'); if (el) el.value = s.vvtKi; }
                    if (s.vvtKd !== undefined) { ecu.vvtKd = s.vvtKd; const el = document.getElementById('inp-vvt-kd'); if (el) el.value = s.vvtKd; }
                    const _impImp = typeof useMetric !== 'undefined' && !useMetric;
                    // Support both new kPa fields and legacy PSI field names
                    const _wgKpa   = s.wgSpringKpa           ?? (s.wgSpringPsi           != null ? s.wgSpringPsi           * 6.89476 : 48.3);
                    const _bcKpa   = s.boostCutKpa           ?? (s.boostCutPsi            != null ? s.boostCutPsi            * 6.89476 : 103.4);
                    const _bchKpa  = s.boostCutHysteresisKpa ?? (s.boostCutHysteresisPsi  != null ? s.boostCutHysteresisPsi  * 6.89476 : 13.8);
                    if (ecu) { ecu.wgSpringKpa = _wgKpa; ecu.boostCutKpa = _bcKpa; ecu.boostCutHysteresisKpa = _bchKpa; }
                    const elWgSpring = document.getElementById('inp-wg-spring'); if (elWgSpring) elWgSpring.value = _impImp ? parseFloat((_wgKpa  * 0.14504).toFixed(1)) : _wgKpa;
                    const elBoostCut = document.getElementById('inp-boost-cut'); if (elBoostCut) elBoostCut.value = _impImp ? parseFloat((_bcKpa  * 0.14504).toFixed(1)) : _bcKpa;
                    const elBoostCutHyst = document.getElementById('inp-boost-cut-hyst'); if (elBoostCutHyst) elBoostCutHyst.value = _impImp ? parseFloat((_bchKpa * 0.14504).toFixed(1)) : _bchKpa;
                    if (s.clBoostEnabled !== undefined) { ecu.clBoostEnabled = s.clBoostEnabled; const el = document.getElementById('inp-cl-boost-enabled'); if (el) { el.checked = s.clBoostEnabled; updateClBoostUI(); } }
                    if (s.clBoostKp !== undefined) { ecu.clBoostKp = s.clBoostKp; const el = document.getElementById('inp-cl-boost-kp'); if (el) el.value = s.clBoostKp; }
                    if (s.clBoostKi !== undefined) { ecu.clBoostKi = s.clBoostKi; const el = document.getElementById('inp-cl-boost-ki'); if (el) el.value = s.clBoostKi; }
                    if (s.clBoostKd !== undefined) { ecu.clBoostKd = s.clBoostKd; const el = document.getElementById('inp-cl-boost-kd'); if (el) el.value = s.clBoostKd; }
                    const elScPulley = document.getElementById('inp-sc-pulley'); if (elScPulley) elScPulley.value = s.scPulleyRatio ?? 2.5;
                    const elScBypass = document.getElementById('inp-sc-bypass'); if (elScBypass) elScBypass.value = s.scBypassThreshold ?? 15.0;
                    if (s.scPulleyRatio !== undefined && ecu) ecu.scPulleyRatio = s.scPulleyRatio;
                    if (s.scBypassThreshold !== undefined && ecu) ecu.scBypassThreshold = s.scBypassThreshold;
                    // Nitrous settings
                    if (s.nitrousEnabled !== undefined) {
                        ecu.nitrousEnabled = s.nitrousEnabled;
                        ecu.nitrousShotType = s.nitrousShotType || 'dry';
                        ecu.nitrousJetSize = s.nitrousJetSize ?? 28;
                        ecu.nitrousCalculatedHp = Math.pow(ecu.nitrousJetSize, 2) * 0.043;
                        ecu.nitrousActivationMode = s.nitrousActivationMode || 'switch';
                        ecu.nitrousFuelJetSize = s.nitrousFuelJetSize ?? 28;
                        ecu.nitrousMinTps = s.nitrousMinTps ?? 80;
                        ecu.nitrousMinRpm = s.nitrousMinRpm ?? 2500;
                        ecu.nitrousMaxRpm = s.nitrousMaxRpm ?? 7000;
                        ecu.nitrousMaxMapKpa = s.nitrousMaxMapKpa ?? 150;
                        ecu.nitrousAfrCutLean = s.nitrousAfrCutLean ?? 13.5;
                        ecu.nitrousVeCompPct = s.nitrousVeCompPct ?? 0;
                        ecu.nitrousTimingRetard = s.nitrousTimingRetard ?? 0;
                        const elN2oJet = document.getElementById('inp-nitrous-jet'); if (elN2oJet) elN2oJet.value = ecu.nitrousJetSize;
                        const elN2oMode = document.getElementById('inp-nitrous-mode'); if (elN2oMode) elN2oMode.value = ecu.nitrousActivationMode;
                        const elN2oFuel = document.getElementById('inp-nitrous-fuel-jet'); if (elN2oFuel) elN2oFuel.value = ecu.nitrousFuelJetSize;
                        const elN2oMinTps = document.getElementById('inp-nitrous-min-tps'); if (elN2oMinTps) elN2oMinTps.value = ecu.nitrousMinTps;
                        const elN2oMinRpm = document.getElementById('inp-nitrous-min-rpm'); if (elN2oMinRpm) elN2oMinRpm.value = ecu.nitrousMinRpm;
                        const elN2oMaxRpm = document.getElementById('inp-nitrous-max-rpm'); if (elN2oMaxRpm) elN2oMaxRpm.value = ecu.nitrousMaxRpm;
                        const elN2oMaxMap = document.getElementById('inp-nitrous-max-map'); if (elN2oMaxMap) elN2oMaxMap.value = ecu.nitrousMaxMapKpa;
                        const elN2oAfrCut = document.getElementById('inp-nitrous-afr-cut'); if (elN2oAfrCut) elN2oAfrCut.value = ecu.nitrousAfrCutLean;
                        const elN2oVeComp = document.getElementById('inp-nitrous-ve-comp'); if (elN2oVeComp) elN2oVeComp.value = ecu.nitrousVeCompPct;
                        const elN2oRetard = document.getElementById('inp-nitrous-retard'); if (elN2oRetard) elN2oRetard.value = ecu.nitrousTimingRetard;
                        const hpLbl = document.getElementById('lbl-nitrous-hp'); if (hpLbl) hpLbl.innerText = `≈ ${ecu.nitrousCalculatedHp.toFixed(1)} HP`;
                    }
                    const elTrigOffset2 = document.getElementById('inp-trigger-offset');
                    if (elTrigOffset2) elTrigOffset2.value = s.triggerAngleOffset ?? 0;
                    const elFixedTiming2 = document.getElementById('inp-fixed-timing');
                    if (elFixedTiming2) elFixedTiming2.value = s.fixedTimingAngle ?? 10;
                    if (s.fuelPressureModelEnabled !== undefined) {
                        const elFPM = document.getElementById('inp-fuel-pressure-model');
                        if (elFPM) elFPM.checked = s.fuelPressureModelEnabled;
                    }
                    { // baseFuelPressure — new kPa field; fall back to legacy PSI field
                        const _bfpKpa = s.baseFuelPressureKpa ?? (s.baseFuelPressurePsi != null ? s.baseFuelPressurePsi * 6.89476 : undefined);
                        if (_bfpKpa !== undefined) {
                            if (ecu) ecu.baseFuelPressureKpa = _bfpKpa;
                            const elFP = document.getElementById('inp-base-fuel-pressure');
                            if (elFP) elFP.value = (typeof useMetric !== 'undefined' && !useMetric)
                                ? parseFloat((_bfpKpa * 0.14504).toFixed(1))
                                : _bfpKpa;
                        }
                    }
                    if (s.fuelSystemType !== undefined) {
                        const elFST = document.getElementById('inp-fuel-system-type');
                        if (elFST) elFST.value = s.fuelSystemType;
                    }
                    if (s.injectorReferencePressureKpa !== undefined) {
                        const elIRP = document.getElementById('inp-inj-ref-pressure');
                        if (elIRP) elIRP.value = (typeof useMetric !== 'undefined' && !useMetric)
                            ? parseFloat((s.injectorReferencePressureKpa * 0.14504).toFixed(1))
                            : s.injectorReferencePressureKpa;
                    }
                    // Rev limit extras
                    if (s.revLimitHysteresis !== undefined) { const el = document.getElementById('inp-rev-limit-hysteresis'); if (el) el.value = s.revLimitHysteresis; }
                    if (s.revLimitFuelCut !== undefined) { const el = document.getElementById('inp-rev-fuel-cut'); if (el) el.checked = s.revLimitFuelCut; }
                    if (s.revLimitSparkCut !== undefined) { const el = document.getElementById('inp-rev-spark-cut'); if (el) el.checked = s.revLimitSparkCut; }
                    // Closed-loop fuel trim
                    if (s.closedLoopEnabled !== undefined) { const el = document.getElementById('inp-cl-enabled'); if (el) el.checked = s.closedLoopEnabled; }
                    if (s.clDeadband !== undefined) { const el = document.getElementById('inp-cl-deadband'); if (el) el.value = (s.clDeadband * 100).toFixed(1); }
                    if (s.clIdleTimeConstant !== undefined) { const el = document.getElementById('inp-cl-idle-tc'); if (el) el.value = s.clIdleTimeConstant; }
                    if (s.clIdleMin !== undefined) { const el = document.getElementById('inp-cl-idle-min'); if (el) el.value = (s.clIdleMin * 100).toFixed(0); }
                    if (s.clIdleMax !== undefined) { const el = document.getElementById('inp-cl-idle-max'); if (el) el.value = (s.clIdleMax * 100).toFixed(0); }
                    if (s.clRunningTimeConstant !== undefined) { const el = document.getElementById('inp-cl-run-tc'); if (el) el.value = s.clRunningTimeConstant; }
                    if (s.clRunningMin !== undefined) { const el = document.getElementById('inp-cl-run-min'); if (el) el.value = (s.clRunningMin * 100).toFixed(0); }
                    if (s.clRunningMax !== undefined) { const el = document.getElementById('inp-cl-run-max'); if (el) el.value = (s.clRunningMax * 100).toFixed(0); }
                    if (s.clWotTimeConstant !== undefined) { const el = document.getElementById('inp-cl-wot-tc'); if (el) el.value = s.clWotTimeConstant; }
                    if (s.clWotMin !== undefined) { const el = document.getElementById('inp-cl-wot-min'); if (el) el.value = (s.clWotMin * 100).toFixed(0); }
                    if (s.clWotMax !== undefined) { const el = document.getElementById('inp-cl-wot-max'); if (el) el.value = (s.clWotMax * 100).toFixed(0); }
                    // O2 delay
                    if (s.o2DelayEnabled !== undefined) { const el = document.getElementById('sim-o2-delay-enabled'); if (el) el.checked = s.o2DelayEnabled; }
                    // Smart alternator
                    if (s.smartAltEnabled !== undefined) { const el = document.getElementById('inp-smart-alt-en'); if (el) el.checked = s.smartAltEnabled; }
                    if (s.smartAltP !== undefined) { const el = document.getElementById('inp-smart-alt-p'); if (el) el.value = s.smartAltP; }
                    if (s.smartAltI !== undefined) { const el = document.getElementById('inp-smart-alt-i'); if (el) el.value = s.smartAltI; }
                    if (s.smartAltD !== undefined) { const el = document.getElementById('inp-smart-alt-d'); if (el) el.value = s.smartAltD; }
                    // Launch control
                    if (s.lcEnabled !== undefined) { const el = document.getElementById('inp-lc-enabled'); if (el) el.checked = s.lcEnabled; }
                    if (s.lcRpm !== undefined) { const el = document.getElementById('inp-lc-rpm'); if (el) el.value = s.lcRpm; }
                    if (s.lcHysteresis !== undefined) { const el = document.getElementById('inp-lc-hyst'); if (el) el.value = s.lcHysteresis; }
                    if (s.lcWindowRpm !== undefined) { const el = document.getElementById('inp-lc-window'); if (el) el.value = s.lcWindowRpm; }
                    if (s.lcCutType !== undefined) { const el = document.getElementById('inp-lc-cut'); if (el) el.value = s.lcCutType; }
                    if (s.lcTimingAbsolute !== undefined) { const el = document.getElementById('inp-lc-timing-limit'); if (el) el.value = s.lcTimingAbsolute; }
                    if (s.lcWindowStartTiming !== undefined) { const el = document.getElementById('inp-lc-timing-start'); if (el) el.value = s.lcWindowStartTiming; }
                    if (s.lcFuelAdder !== undefined) { const el = document.getElementById('inp-lc-fuel'); if (el) el.value = s.lcFuelAdder; }
                    // Ignition system
                    if (s.ignitionMode !== undefined) { const el = document.getElementById('inp-ign-mode'); if (el) el.value = s.ignitionMode; }
                    if (s.sparkPlugGapIn !== undefined) { const el = document.getElementById('inp-spark-gap'); if (el) el.value = s.sparkPlugGapIn; }
                    // Accel enrichment
                    if (s.accelMode !== undefined) { const el = document.getElementById('inp-accel-mode'); if (el) el.value = s.accelMode; }
                    if (s.accelTime !== undefined) { const el = document.getElementById('inp-accel-time'); if (el) el.value = s.accelTime; }
                    if (s.accelDecayRate !== undefined) { const el = document.getElementById('inp-accel-decay'); if (el) el.value = s.accelDecayRate; }
                    // Knock control
                    if (s.knockAttackDeg !== undefined) { const el = document.getElementById('inp-knk-attack'); if (el) el.value = s.knockAttackDeg; }
                    if (s.knockMaxRetardDeg !== undefined) { const el = document.getElementById('inp-knk-max-retard'); if (el) el.value = s.knockMaxRetardDeg; }
                    if (s.knockRecoveryDegPerRev !== undefined) { const el = document.getElementById('inp-knk-recovery'); if (el) el.value = s.knockRecoveryDegPerRev; }
                    if (s.knockMinTps !== undefined) { const el = document.getElementById('inp-knk-min-tps'); if (el) el.value = s.knockMinTps; }
                    if (s.knockMinMap !== undefined) { const el = document.getElementById('inp-knk-min-map'); if (el) el.value = s.knockMinMap; }
                    if (s.knockCenterFreq !== undefined) {
                        ecu.knockCenterFreq = s.knockCenterFreq;
                        const el = document.getElementById('inp-knk-center-freq'); if (el) el.value = s.knockCenterFreq;
                        const r  = document.getElementById('inp-knk-center-freq-range'); if (r) r.value = s.knockCenterFreq;
                    }
                    if (s.knockCalibrated !== undefined) {
                        ecu.knockCalibrated = s.knockCalibrated;
                        syncKnockCalibrationState();
                        if (s.knockCalibrated && s.knockEnabled !== undefined) {
                            const el = document.getElementById('inp-knk-enabled');
                            if (el) {
                                el.checked = s.knockEnabled;
                                const params = document.getElementById('knock-control-params');
                                if (params) params.classList.toggle('hidden', !s.knockEnabled);
                            }
                        }
                    } else if (s.knockEnabled !== undefined) {
                        const el = document.getElementById('inp-knk-enabled'); if (el) el.checked = s.knockEnabled;
                    }
                    updateIdleSettings();
                    renderAllTables();
                }

                if (loadedTune.active_mods) {
                    renderUpgradesUI(loadedTune.active_mods);
                }

        alert("Tune loaded successfully!");
        e.target.value = '';
    } catch (err) {
        alert("Failed to load tune: " + err.message);
        e.target.value = '';
    }
}

function importTuneBinary(buf, e) {
    try {
        if (!ECU_MEMORY_MAP) { ensureMemoryMap(() => importTuneBinary(buf, e)); return; }
        const view = new DataView(buf);
        const magic = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
        if (magic !== 'XSIM') { alert('Not a valid AlphaX tune file (bad header).'); return; }

        for (const field of ECU_MEMORY_MAP) {
            const { name, offset, type, scale, dimensions } = field;
            const totalEls = dimensions.reduce((a, b) => a * b, 1);
            const vals = [];
            let off = offset;
            for (let i = 0; i < totalEls; i++) {
                let v;
                if      (type === 'uint8'  || type === 'bool') { v = view.getUint8(off);            off += 1; }
                else if (type === 'int8')                      { v = view.getInt8(off);              off += 1; }
                else if (type === 'uint16')                    { v = view.getUint16(off, true);       off += 2; }
                else if (type === 'int16')                     { v = view.getInt16(off, true);        off += 2; }
                else if (type === 'float32')                   { v = view.getFloat32(off, true);      off += 4; }
                vals.push(_round1dp(type === 'float32' ? v : v * scale));
            }
            let result = vals.length === 1 ? vals[0] : vals;
            if (dimensions.length === 2) {
                // Binary is column-major (X/RPM outer, MAP inner) — reconstruct row-major [MAP][RPM]
                const [rows, cols] = dimensions;
                result = Array.from({ length: rows }, (_, r) =>
                    Array.from({ length: cols }, (_, c) => vals[c * rows + r])
                );
            }
            if (ECU_ENUM_DECODE[name] !== undefined) result = ECU_ENUM_DECODE[name][result] ?? result;
            if (type === 'bool') result = !!result;
            ecu[ECU_FIELD_MAP[name] ?? name] = result;
        }

        // Guard: old .bin files have 0xFF fill at offsets added later.
        // int16 0xFFFF = -1; uint16 0xFFFF = 65535 — neither is a valid RPM or °C value.
        if (!ecu.idleTargetRpmAxis || ecu.idleTargetRpmAxis.every(v => v === -1))
            ecu.idleTargetRpmAxis = [20, 40, 60, 70, 80, 90, 100, 110];
        if (!ecu.idleTargetRpmCurve || ecu.idleTargetRpmCurve.every(v => v === 65535))
            ecu.idleTargetRpmCurve = [950, 900, 875, 860, 850, 850, 850, 850];
        if (!ecu.maxIdleTpsPct || ecu.maxIdleTpsPct > 100)
            ecu.maxIdleTpsPct = 10;
        if (!ecu.idleRpmUpperLimit || ecu.idleRpmUpperLimit > 10000)
            ecu.idleRpmUpperLimit = 200;

        populateSettingsUI();   // ECU → DOM first, so updateIdleSettings won't overwrite scalars
        updateIdleSettings();   // DOM → ECU (now DOM has correct values)
        renderAllTables();
        alert('Tune loaded from binary!');
        e.target.value = '';
    } catch (err) {
        alert('Failed to load binary tune: ' + err.message);
        e.target.value = '';
    }
}

const fileTuneUpload = document.getElementById('file-tune-upload');
if (fileTuneUpload) {
    fileTuneUpload.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        if (file.name.endsWith('.bin')) {
            reader.readAsArrayBuffer(file);
            reader.onload = (ev) => importTuneBinary(ev.target.result, e);
        } else {
            reader.readAsText(file);
            reader.onload = (ev) => importTuneJson(JSON.parse(ev.target.result), e);
        }
    });
}

const fileMemmapUpload = document.getElementById('file-memmap-upload');
if (fileMemmapUpload) {
    fileMemmapUpload.addEventListener('change', (ev) => {
        const file = ev.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.readAsText(file);
        reader.onload = (e) => {
            try {
                ECU_MEMORY_MAP = JSON.parse(e.target.result);
                const action = _pendingBinaryAction;
                _pendingBinaryAction = null;
                if (action) action();
            } catch (err) {
                alert('Failed to load memory map: ' + err.message);
            }
            ev.target.value = '';
        };
    });
}

// --- MOD FILE UPLOAD ---

const fileModUpload = document.getElementById('file-mod-upload');
if (fileModUpload) {
    fileModUpload.addEventListener('change', async (e) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const targetCat = e.target.getAttribute('data-target-cat');
        let firstLoadedModId = null;
        let loadedCount = 0;

        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            try {
                const text = await file.text();
                const mod = JSON.parse(text);
                if (mod.schema_type !== "mod_profile" || !mod.mod_id) {
                    console.warn(`Skipped ${file.name}: Invalid mod profile schema.`);
                    continue;
                }
                sampleModsDb[mod.mod_id] = mod;
                if (!firstLoadedModId) firstLoadedModId = mod.mod_id;
                loadedCount++;
            } catch (err) {
                console.error(`Failed to parse ${file.name}:`, err);
            }
        }

        if (loadedCount > 0) {
            renderUpgradesUI();
            if (targetCat && firstLoadedModId) {
                const select = document.getElementById(`mod-select-${targetCat.toLowerCase()}`);
                if (select) {
                    select.value = firstLoadedModId;
                    applyVehicleMods();
                }
            }
            alert(`Successfully loaded ${loadedCount} mod(s)!`);
        }

        e.target.value = '';
        e.target.removeAttribute('data-target-cat');
    });
}

// --- MOD LIST EXPORT / IMPORT ---

function exportModList() {
    const categories = ['Intake', 'Intake Manifold', 'Exhaust Manifold', 'Exhaust', 'Port and Polish', 'Pistons', 'Rods', 'Clutch', 'Turbocharger', 'Supercharger', 'Camshaft', 'Cooling', 'FuelPump', 'Injector'];
    const selections = {};
    const modsToExport = {};

    categories.forEach(cat => {
        const sel = document.getElementById(`mod-select-${cat.toLowerCase()}`);
        const val = sel ? sel.value : '';
        if (val && !val.includes('.xsim-mod') && val !== 'custom' && sampleModsDb[val]) {
            selections[cat.toLowerCase()] = val;
            modsToExport[val] = sampleModsDb[val];
        }
    });

    const nitrousSel = document.getElementById('mod-select-nitrous');
    selections.nitrous = nitrousSel ? nitrousSel.value : '';
    selections.twin_turbo = twinTurboEnabled;

    const isShopify = !!window.shopifyCustomer;
    const payload = {
        schema_type: 'mod_list',
        version: '1.0',
        mods: isShopify ? Object.keys(modsToExport) : Object.values(modsToExport),
        selections
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'modlist.xsim-modlst';
    a.click();
    URL.revokeObjectURL(a.href);
}

const fileModListUpload = document.getElementById('file-modlist-upload');
if (fileModListUpload) {
    fileModListUpload.addEventListener('change', async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            const text = await file.text();
            const data = JSON.parse(text);
            if (data.schema_type !== 'mod_list' || !Array.isArray(data.mods)) {
                alert('Invalid mod list file.');
                return;
            }
            data.mods.forEach(m => {
                if (typeof m === 'object' && m !== null && m.mod_id) sampleModsDb[m.mod_id] = m;
            });

            renderUpgradesUI();

            const sel = data.selections || {};
            const categories = ['Intake', 'Intake Manifold', 'Exhaust Manifold', 'Exhaust', 'Port and Polish', 'Pistons', 'Rods', 'Clutch', 'Turbocharger', 'Supercharger', 'Camshaft', 'Cooling', 'FuelPump', 'Injector'];
            categories.forEach(cat => {
                const modId = sel[cat.toLowerCase()];
                if (!modId) return;
                const dropdown = document.getElementById(`mod-select-${cat.toLowerCase()}`);
                if (dropdown && dropdown.querySelector(`option[value="${modId}"]`)) {
                    dropdown.value = modId;
                }
            });

            const nitrousSel = document.getElementById('mod-select-nitrous');
            if (nitrousSel && sel.nitrous !== undefined) nitrousSel.value = sel.nitrous;

            if (typeof sel.twin_turbo === 'boolean') {
                twinTurboEnabled = sel.twin_turbo;
                const chkTt = document.getElementById('chk-twin-turbo');
                if (chkTt) chkTt.checked = twinTurboEnabled;
            }

            applyVehicleMods();
            alert(`Mod list loaded successfully (${data.mods.length} mod${data.mods.length !== 1 ? 's' : ''}).`);
        } catch (err) {
            console.error('Failed to import mod list:', err);
            alert('Failed to import mod list: ' + err.message);
        }
        e.target.value = '';
    });
}

// Ignition mode dropdown: update cap visibility immediately on change (before Apply is pressed)
const ignModeSelect = document.getElementById('inp-ign-mode');
if (ignModeSelect) {
    ignModeSelect.addEventListener('change', () => {
        ecu.ignitionMode = ignModeSelect.value;
        const wrap = document.getElementById('distributor-cap-wrap');
        if (wrap) wrap.classList.toggle('hidden', ecu.ignitionMode !== 'distributor');
    });
}

// --- TABLE CELL SELECTION & INTERACTION ---

document.addEventListener('mousedown', (e) => {
    if (appMode !== 'garage') return;
    const td = e.target.closest('td[data-r]');
    if (td) {
        const wrap = td.closest('[id^="container-"]');
        if (wrap) {
            activeTableId = wrap.id.replace('container-', '').replace('-table', '');
        } else {
            activeTableId = null;
        }

        if (e.shiftKey && selStart) selEnd = getCellCoords(td);
        else { selStart = getCellCoords(td); selEnd = getCellCoords(td); }

        isDragging = true;
        renderSelection();
        hideInlineEditor();
        e.preventDefault();
        return;
    }

    const th = e.target.closest('th[data-axis]');
    if (th) {
        const wrap = th.closest('[id^="container-"]');
        if (!wrap) return;
        const tId = wrap.id.replace('container-', '').replace('-table', '').replace('-curve', '');
        const axisType = th.getAttribute('data-axis');
        const idx = parseInt(th.getAttribute('data-idx'));
        axisEditContext = { type: axisType, idx, tableId: tId };
        activeTableId = null;
        renderSelection();

        const rect = th.getBoundingClientRect();
        if (inlineEditor) {
            inlineEditor.style.top = `${rect.top}px`;
            inlineEditor.style.left = `${rect.left}px`;
            inlineEditor.style.width = `${Math.max(rect.width, 44)}px`;
            inlineEditor.style.height = `${rect.height}px`;
            inlineEditor.classList.remove('hidden');
        }
        if (inlineInput) {
            const axes = getAxesForTable(tId);
            inlineInput.value = (axisType === 'col') ? axes.cAxis[idx] : axes.rAxis[idx];
            inlineInput.select();
            inlineInput.focus();
        }
        e.preventDefault();
        return;
    }

    if (!e.target.closest('#inline-editor') && !e.target.closest('.tune-table') && !e.target.closest('#table-tools')) {
        activeTableId = null;
        renderSelection();
        hideInlineEditor();
    }
});

document.addEventListener('mouseover', (e) => {
    if (isDragging && appMode === 'garage') {
        const td = e.target.closest('td[data-r]');
        if (td && activeTableId) {
            selEnd = getCellCoords(td);
            renderSelection();
        }
    }
});

document.addEventListener('mouseup', () => isDragging = false);

if (inlineInput) {
    inlineInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') { applyEdit(); e.preventDefault(); }
        else if (e.key === 'Escape') hideInlineEditor();
    });
}

{
    const mathValInput = document.getElementById('inp-math-val');
    if (mathValInput) {
        mathValInput.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                const op = pendingMathOp || '+';
                pendingMathOp = null;
                applyMathToSelection(op);
                mathValInput.blur();
                e.preventDefault();
                e.stopPropagation();
            } else if (e.key === 'Escape') {
                pendingMathOp = null;
                mathValInput.blur();
            }
        });
    }
}

// Enter key in settings inputs triggers the nearest Apply button
document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter') return;
    if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'SELECT') return;
    const panel = e.target.closest('.tab-content, [id^="view-"]');
    if (!panel) return;
    const applyBtn = panel.querySelector('.setting-apply-btn');
    if (applyBtn) { applyBtn.click(); e.preventDefault(); }
});

window.addEventListener('scroll', hideInlineEditor, true);

// --- TABLE KEYBOARD SHORTCUTS (copy, navigate, type-to-edit) ---

document.addEventListener('keydown', (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

    // Ctrl+Z: undo, Ctrl+Y / Ctrl+Shift+Z: redo
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        undo(); e.preventDefault(); return;
    }
    if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y' || (e.shiftKey && e.key.toLowerCase() === 'z'))) {
        redo(); e.preventDefault(); return;
    }

    // Ctrl+C: copy selection as TSV
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
        if (!activeTableId || !selStart || !selEnd || appMode !== 'garage') return;
        const rMin = Math.min(selStart.r, selEnd.r);
        const rMax = Math.max(selStart.r, selEnd.r);
        const cMin = Math.min(selStart.c, selEnd.c);
        const cMax = Math.max(selStart.c, selEnd.c);
        const tableData = getActiveTableData();
        let tsv = "";
        for (let r = rMin; r <= rMax; r++) {
            let rowVals = [];
            for (let c = cMin; c <= cMax; c++) rowVals.push(tableData[r][c]);
            tsv += rowVals.join("\t") + "\n";
        }
        const ta = document.createElement("textarea");
        ta.value = tsv.trim();
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        document.querySelectorAll('.cell-selected').forEach(td => {
            const oldBg = td.style.backgroundColor;
            td.style.backgroundColor = '#3B82F6';
            setTimeout(() => td.style.backgroundColor = oldBg, 150);
        });
        e.preventDefault();
        return;
    }

    if (!activeTableId || !selStart || !selEnd || appMode !== 'garage') return;
    if (inlineEditor && !inlineEditor.classList.contains('hidden')) return;

    // Arrow key navigation
    if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
        e.preventDefault();
        const axes = getActiveAxes();
        const rMaxLimit = axes.rAxis.length - 1;
        const cMaxLimit = axes.cAxis.length - 1;
        let dR = 0, dC = 0;
        if (e.key === 'ArrowUp') dR = 1;
        if (e.key === 'ArrowDown') dR = -1;
        if (e.key === 'ArrowLeft') dC = -1;
        if (e.key === 'ArrowRight') dC = 1;
        let nextR = Math.max(0, Math.min(selEnd.r + dR, rMaxLimit));
        let nextC = Math.max(0, Math.min(selEnd.c + dC, cMaxLimit));
        selEnd = { r: nextR, c: nextC };
        if (!e.shiftKey) selStart = { r: nextR, c: nextC };
        renderSelection();
        return;
    }

    // > / < : add or subtract 1 from selection
    if (e.key === '>') { applyDeltaToSelection(1); e.preventDefault(); return; }
    if (e.key === '<') { applyDeltaToSelection(-1); e.preventDefault(); return; }

    // H / V / I : interpolation shortcuts
    if (!e.ctrlKey && !e.metaKey && !e.altKey) {
        const lk = e.key.toLowerCase();
        if (lk === 'h') { interpolateSelection('x'); e.preventDefault(); return; }
        if (lk === 'v') { interpolateSelection('y'); e.preventDefault(); return; }
        if (lk === 'i') { interpolateSelection('2d'); e.preventDefault(); return; }
    }

    // + / - / * : focus math input and tag which operation to apply on Enter
    if (e.key === '+' || e.key === '-' || e.key === '*') {
        pendingMathOp = e.key;
        const mathInput = document.getElementById('inp-math-val');
        if (mathInput) { mathInput.focus(); mathInput.select(); }
        e.preventDefault();
        return;
    }

    // Type a number or decimal to open inline editor (-  now handled above)
    if (e.key.length === 1 && /[0-9.]/.test(e.key)) {
        showInlineEditor(e.key);
        e.preventDefault();
    } else if (e.key === 'Enter') {
        const tableData = getActiveTableData();
        if (tableData) showInlineEditor(tableData[selStart.r][selStart.c]);
        e.preventDefault();
    }
});

// --- PASTE FROM SPREADSHEET ---

document.addEventListener('paste', (e) => {
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    if (appMode !== 'garage' || !activeTableId || !selStart || !selEnd) return;

    const pasteData = e.clipboardData.getData('text');
    if (!pasteData) return;
    saveUndoState();

    const rows = pasteData.trim().split(/\r?\n/);
    const tableData = getActiveTableData();
    const axes = getActiveAxes();
    const maxR = axes.rAxis.length - 1;
    const maxC = axes.cAxis.length - 1;
    const startR = Math.min(selStart.r, selEnd.r);
    const startC = Math.min(selStart.c, selEnd.c);
    const suffix = activeTableId.endsWith('-curve') ? '' : '-table';
    const tableContainer = document.getElementById(`container-${activeTableId}${suffix}`);

    for (let i = 0; i < rows.length; i++) {
        const cols = rows[i].split('\t');
        const r = startR + i;
        if (r > maxR) break;
        for (let j = 0; j < cols.length; j++) {
            const c = startC + j;
            if (c > maxC) break;
            let val = parseFloat(cols[j]);
            if (!isNaN(val)) {
                if (activeTableId === 'cranking-duty-curve') val = Math.min(val, 100);
                tableData[r][c] = val;
                const td = tableContainer.querySelector(`td[data-r="${r}"][data-c="${c}"]`);
                if (td) {
                    td.innerText = val;
                    td.style.backgroundColor = '#10B981';
                    setTimeout(() => td.style.backgroundColor = '', 400);
                }
            }
        }
    }
    refreshHeatmap(activeTableId);
    selEnd = {
        r: Math.min(startR + rows.length - 1, maxR),
        c: Math.min(startC + rows[0].split('\t').length - 1, maxC)
    };
    renderSelection();
    e.preventDefault();
});

// --- TEMPERATURE UNIT TOGGLE ---

function applyTempUnitChange(nowF) {
    if (typeof tempUnitF === 'undefined') return;
    const input = document.getElementById('sim-ambient-temp');
    const lbl   = document.getElementById('lbl-ambient-temp');
    if (!input) return;
    const cur = parseFloat(input.value);
    if (!isNaN(cur)) {
        if (nowF && !tempUnitF) {
            // was °C, switching to °F
            input.value = Math.round(cur * 9 / 5 + 32);
            input.dataset.unit = 'F';
        } else if (!nowF && tempUnitF) {
            // was °F, switching to °C
            input.value = Math.round((cur - 32) * 5 / 9);
            input.dataset.unit = 'C';
        }
    }
    if (lbl) {
        const key = nowF ? 'ambient_temperature_f' : 'ambient_temperature_c';
        lbl.setAttribute('data-i18n', 'dynosim.auto.' + key);
        lbl.setAttribute('data-t', key);
        lbl.textContent = T(key, nowF ? 'Ambient Temperature (°F)' : 'Ambient Temperature (°C)');
    }
    tempUnitF = nowF;
}

document.querySelectorAll('input[name="sim-unit-system"]').forEach(radio => {
    radio.addEventListener('change', () => {
        const isMetric = document.getElementById('sim-unit-metric')?.checked === true;
        const wasMetric = typeof useMetric !== 'undefined' ? useMetric : false;
        if (typeof useMetric !== 'undefined') useMetric = isMetric;
        applyTempUnitChange(!isMetric);

        // Convert injector reference pressure input
        const injInput = document.getElementById('inp-inj-ref-pressure');
        if (injInput) {
            const cur = parseFloat(injInput.value);
            if (!isNaN(cur)) {
                if (isMetric && !wasMetric) injInput.value = Math.round(cur / 0.14504);    // PSI → kPa
                else if (!isMetric && wasMetric) injInput.value = (cur * 0.14504).toFixed(1); // kPa → PSI
            }
        }
        const injLbl = document.getElementById('lbl-inj-ref-pressure');
        if (injLbl) injLbl.textContent = isMetric ? 'Injector Reference Pressure (kPa)' : 'Injector Reference Pressure (PSI)';

        // Convert boost / fuel pressure inputs
        [
            { inpId: 'inp-base-fuel-pressure', lblId: 'lbl-base-fuel-pressure', lblMetric: 'Base Fuel Pressure (kPa)', lblImp: 'Base Fuel Pressure (PSI)' },
            { inpId: 'inp-wg-spring',          lblId: 'lbl-wg-spring',          lblMetric: 'Wastegate Spring (kPa)',   lblImp: 'Wastegate Spring (psi)' },
            { inpId: 'inp-boost-cut',          lblId: 'lbl-boost-cut',          lblMetric: 'Boost Cut (kPa)',          lblImp: 'Boost Cut (psi)' },
            { inpId: 'inp-boost-cut-hyst',     lblId: 'lbl-boost-cut-hyst',     lblMetric: 'Hysteresis (kPa)',         lblImp: 'Hysteresis (psi)' },
        ].forEach(({ inpId, lblId, lblMetric, lblImp }) => {
            const inp = document.getElementById(inpId);
            if (inp) {
                const cur = parseFloat(inp.value);
                if (!isNaN(cur)) {
                    if (isMetric && !wasMetric) inp.value = Math.round(cur / 0.14504);         // PSI → kPa
                    else if (!isMetric && wasMetric) inp.value = (cur * 0.14504).toFixed(1);   // kPa → PSI
                }
            }
            const lbl = document.getElementById(lblId);
            if (lbl) lbl.textContent = isMetric ? lblMetric : lblImp;
        });

        // Update IAT axis unit labels
        const tUnitText = isMetric ? '°C' : '°F';
        ['lbl-iat-ve-unit', 'lbl-iat-timing-unit'].forEach(id => {
            const el = document.getElementById(id);
            if (el) el.textContent = tUnitText;
        });

        if (typeof ecu !== 'undefined') {
            ecu.displayLambda = isMetric;
            const lambdaCb = document.getElementById('inp-display-lambda');
            if (lambdaCb) lambdaCb.checked = isMetric;
        }
        const speedUnit = document.getElementById('drag-hud-speed-unit');
        if (speedUnit) speedUnit.textContent = isMetric ? 'KPH' : 'MPH';

        // Axis wizard MAP inputs and labels
        const wBase = document.getElementById('wizard-map-base');
        const wMax  = document.getElementById('wizard-map-max');
        if (wBase && wMax) {
            const wb = parseFloat(wBase.value), wm = parseFloat(wMax.value);
            if (!isNaN(wb) && !isNaN(wm)) {
                if (isMetric && !wasMetric)       { wBase.value = Math.round(wb / 0.14504);        wMax.value = Math.round(wm / 0.14504); }
                else if (!isMetric && wasMetric)  { wBase.value = (wb * 0.14504).toFixed(1); wMax.value = (wm * 0.14504).toFixed(1); }
            }
        }
        _setWizardMapLabels(isMetric);
        updateWizardPreviews();

        // Fuel rail pressure placeholder when engine is off
        if (typeof isEngineRunning !== 'undefined' && !isEngineRunning && typeof ecu !== 'undefined' && ecu.fuelPressureModelEnabled) {
            const fpEl = document.getElementById('data-fuel-pressure');
            if (fpEl) fpEl.textContent = `-- ${isMetric ? 'kPa' : 'PSI'}`;
        }

        if (typeof renderAllTables === 'function') renderAllTables();
    });
});

document.querySelectorAll('input[name="sim-map-mode"]').forEach(radio => {
    radio.addEventListener('change', () => {
        if (typeof mapDisplayGauge !== 'undefined') {
            mapDisplayGauge = document.getElementById('sim-map-gauge')?.checked === true;
        }
        if (typeof renderAllTables === 'function') renderAllTables();
    });
});

function onDynoModeChange() {
    const mode = document.getElementById('dyno-mode').value;
    dynoMode = mode;
    document.getElementById('dyno-load-opts').classList.toggle('hidden', mode !== 'load');
    document.getElementById('dyno-lock-opts').classList.toggle('hidden', mode !== 'rpm-lock');
}


// --- AXIS WIZARD ---

// Returns the two bracketing indices for val in axis.
// When val is beyond the edge, returns the last two indices so that
// t > 1 or t < 0 produces a linear extrapolation from the edge trend.
function _axisBracket(val, axis) {
    if (val <= axis[0]) return [0, 1];
    if (val >= axis[axis.length - 1]) return [axis.length - 2, axis.length - 1];
    for (let i = 0; i < axis.length - 1; i++) {
        if (val >= axis[i] && val <= axis[i + 1]) return [i, i + 1];
    }
    return [axis.length - 2, axis.length - 1];
}

// Bilinear interpolation/extrapolation from an old grid onto a single (rpm, map) point.
// t values outside [0,1] give linear extrapolation from the edge trend.
function _extrap2D(rpm, map, oldRpmAxis, oldMapAxis, oldTable) {
    const [ri0, ri1] = _axisBracket(map, oldMapAxis);
    const [ci0, ci1] = _axisBracket(rpm, oldRpmAxis);
    const ty = (oldMapAxis[ri1] !== oldMapAxis[ri0])
        ? (map - oldMapAxis[ri0]) / (oldMapAxis[ri1] - oldMapAxis[ri0]) : 0;
    const tx = (oldRpmAxis[ci1] !== oldRpmAxis[ci0])
        ? (rpm - oldRpmAxis[ci0]) / (oldRpmAxis[ci1] - oldRpmAxis[ci0]) : 0;
    const q11 = oldTable[ri0][ci0], q21 = oldTable[ri0][ci1];
    const q12 = oldTable[ri1][ci0], q22 = oldTable[ri1][ci1];
    const r1 = q11 * (1 - tx) + q21 * tx;
    const r2 = q12 * (1 - tx) + q22 * tx;
    return r1 * (1 - ty) + r2 * ty;
}

// Resamples a 2D table from oldRpmAxis/oldMapAxis onto newRpmAxis/newMapAxis,
// interpolating within the old range and extrapolating linearly beyond it.
function resampleTableData(oldRpmAxis, oldMapAxis, oldTable, newRpmAxis, newMapAxis, round = 1) {
    return newMapAxis.map((map) =>
        newRpmAxis.map((rpm) => {
            const v = _extrap2D(rpm, map, oldRpmAxis, oldMapAxis, oldTable);
            return Math.round(v * round) / round;
        })
    );
}

function initUnitLabels() {
    const isImp = typeof useMetric !== 'undefined' && !useMetric;
    const injLbl = document.getElementById('lbl-inj-ref-pressure');
    if (injLbl) injLbl.textContent = isImp ? 'Injector Reference Pressure (PSI)' : 'Injector Reference Pressure (kPa)';
    const injInp = document.getElementById('inp-inj-ref-pressure');
    if (injInp && typeof ecu !== 'undefined') {
        injInp.value = isImp
            ? parseFloat((ecu.injectorReferencePressureKpa * 0.14504).toFixed(1))
            : ecu.injectorReferencePressureKpa;
    }
    const wBase = document.getElementById('wizard-map-base');
    const wMax  = document.getElementById('wizard-map-max');
    if (wBase && isImp) wBase.value = (20  * 0.14504).toFixed(1);
    if (wMax  && isImp) wMax.value  = (180 * 0.14504).toFixed(1);
    _setWizardMapLabels(!isImp);
    updateWizardPreviews();
}

function _setWizardMapLabels(isMetric) {
    const s = document.getElementById('wizard-map-section-label');
    const b = document.getElementById('wizard-map-base-label');
    const m = document.getElementById('wizard-map-max-label');
    if (s) s.textContent = isMetric ? 'MAP / Load Axis (16 points, kPa)' : 'MAP / Load Axis (16 points, PSI)';
    if (b) b.textContent = isMetric ? 'Base kPa (min load)' : 'Base PSI (min load)';
    if (m) m.textContent = isMetric ? 'Max kPa' : 'Max PSI';
}

function linearAxis(base, max, count) {
    const result = [];
    const step = (max - base) / (count - 1);
    for (let i = 0; i < count; i++) {
        result.push(Math.round((base + step * i) / 5) * 5);
    }
    return result;
}

function updateWizardPreviews() {
    const rpmBase = parseFloat(document.getElementById('wizard-rpm-base')?.value) || 500;
    const rpmMax  = parseFloat(document.getElementById('wizard-rpm-max')?.value)  || 8000;
    const mapBase = parseFloat(document.getElementById('wizard-map-base')?.value) || 20;
    const mapMax  = parseFloat(document.getElementById('wizard-map-max')?.value)  || 180;
    const rpmPrev = document.getElementById('wizard-rpm-preview');
    const mapPrev = document.getElementById('wizard-map-preview');
    const _mapUnit = (typeof useMetric !== 'undefined' && !useMetric) ? ' PSI' : ' kPa';
    if (rpmPrev) rpmPrev.textContent = linearAxis(rpmBase, rpmMax, 16).join(', ');
    if (mapPrev) mapPrev.textContent = linearAxis(mapBase, mapMax, 16).join(', ') + _mapUnit;
}

function runAxisWizard() {
    const rpmBase = parseFloat(document.getElementById('wizard-rpm-base')?.value);
    const rpmMax  = parseFloat(document.getElementById('wizard-rpm-max')?.value);
    const mapBase = parseFloat(document.getElementById('wizard-map-base')?.value);
    const mapMax  = parseFloat(document.getElementById('wizard-map-max')?.value);

    if ([rpmBase, rpmMax, mapBase, mapMax].some(isNaN)) {
        alert('Please fill in all four axis fields.'); return;
    }
    // Convert MAP inputs to kPa if user is working in imperial (PSI)
    const _wizImp = typeof useMetric !== 'undefined' && !useMetric;
    const mapBaseKpa = _wizImp ? mapBase / 0.14504 : mapBase;
    const mapMaxKpa  = _wizImp ? mapMax  / 0.14504 : mapMax;
    if (rpmMax <= rpmBase || mapMaxKpa <= mapBaseKpa) {
        alert('Max values must be greater than base values.'); return;
    }

    const oldRpmAxis   = ecu.rpmAxis.slice();
    const oldMapAxis   = ecu.mapAxis.slice();
    const oldWgRpmAxis = ecu.wgRpmAxis.slice();
    const oldVvtRpm    = ecu.vvtRpmAxis.slice();
    const oldVvtMap    = ecu.vvtMapAxis.slice();
    const oldVe        = ecu.veTable.map(r => r.slice());
    const oldTiming    = ecu.timingTable.map(r => r.slice());
    const oldAfr       = ecu.targetAfrTable.map(r => r.slice());
    const oldWg        = ecu.wgDutyTable.map(r => r.slice());
    const oldVvtIn          = ecu.vvtIntakeTable.map(r => r.slice());
    const oldVvtEx          = ecu.vvtExhaustTable.map(r => r.slice());
    const oldAltVolt        = ecu.altVoltTable.map(r => r.slice());
    const oldAltVoltRpmAxis = ecu.altVoltRpmAxis.slice();
    const oldAltVoltLoadAxis = ecu.altVoltLoadAxis.slice();

    ecu.rpmAxis = linearAxis(rpmBase, rpmMax, 16);
    ecu.mapAxis = linearAxis(mapBaseKpa, mapMaxKpa, 16);

    // 8-point sub-axes: every other point from the 16-point main axes (2x step size)
    const halfRpm = ecu.rpmAxis.filter((_, i) => i % 2 === 0);
    const halfMap = ecu.mapAxis.filter((_, i) => i % 2 === 0);
    ecu.wgRpmAxis   = halfRpm;
    ecu.vvtRpmAxis  = halfRpm;
    ecu.vvtMapAxis  = halfMap;

    ecu.veTable          = resampleTableData(oldRpmAxis, oldMapAxis, oldVe,     ecu.rpmAxis, ecu.mapAxis, 1);
    ecu.timingTable      = resampleTableData(oldRpmAxis, oldMapAxis, oldTiming, ecu.rpmAxis, ecu.mapAxis, 10);
    ecu.targetAfrTable   = resampleTableData(oldRpmAxis, oldMapAxis, oldAfr,    ecu.rpmAxis, ecu.mapAxis, 10);
    ecu.wgDutyTable      = resampleTableData(oldWgRpmAxis, ecu.tpsAxis, oldWg,  ecu.wgRpmAxis, ecu.tpsAxis, 1);
    ecu.vvtIntakeTable   = resampleTableData(oldVvtRpm, oldVvtMap, oldVvtIn,   ecu.vvtRpmAxis, ecu.vvtMapAxis, 1);
    ecu.vvtExhaustTable  = resampleTableData(oldVvtRpm, oldVvtMap, oldVvtEx,   ecu.vvtRpmAxis, ecu.vvtMapAxis, 1);
    const sparse5 = [0, 4, 8, 11, 15];
    ecu.altVoltRpmAxis  = sparse5.map(i => ecu.rpmAxis[i]);
    ecu.altVoltLoadAxis = sparse5.map(i => ecu.mapAxis[i]);
    ecu.altVoltTable    = resampleTableData(oldAltVoltRpmAxis, oldAltVoltLoadAxis, oldAltVolt, ecu.altVoltRpmAxis, ecu.altVoltLoadAxis, 10);

    renderAllTables();
    alert('Axes updated. Table data interpolated and extrapolated from your existing tune.');
}

function resetAxesToDefault() {
    const _isImp = typeof useMetric !== 'undefined' && !useMetric;
    document.getElementById('wizard-rpm-base').value = 500;
    document.getElementById('wizard-rpm-max').value  = 8000;
    document.getElementById('wizard-map-base').value = _isImp ? (20  * 0.14504).toFixed(1) : 20;
    document.getElementById('wizard-map-max').value  = _isImp ? (180 * 0.14504).toFixed(1) : 180;
    updateWizardPreviews();
    runAxisWizard();
}

// --- Knock Calibration State ---

/**
 * Apply or remove the locked/unlocked visual state on the Enable Knock Control checkbox.
 * Called after calibration is confirmed or a tune is loaded.
 */
function syncKnockCalibrationState() {
    const wrap     = document.getElementById('lbl-knk-enabled-wrap');
    const label    = wrap?.querySelector('label');
    const checkbox = document.getElementById('inp-knk-enabled');
    const hint     = document.getElementById('knk-enabled-hint');
    const calStatus = document.getElementById('knock-cal-status');
    const enterBtn  = document.getElementById('btn-enter-calibration');

    if (ecu.knockCalibrated) {
        if (checkbox) checkbox.disabled = false;
        if (label) {
            label.classList.remove('opacity-50', 'cursor-not-allowed', 'text-gray-500');
            label.classList.add('cursor-pointer', 'text-gray-300');
        }
        if (hint) hint.textContent = 'Timing retards on knock detection and recovers while running clean.';
        // Show calibration status badge if not already visible
        if (calStatus && calStatus.classList.contains('hidden')) {
            calStatus.classList.remove('hidden');
            const freqDisplay = document.getElementById('knk-cal-freq-display');
            if (freqDisplay) freqDisplay.textContent = ecu.knockCenterFreq.toFixed(1);
        }
        // Keep Enter Calibration button hidden while calibrated
        if (enterBtn) enterBtn.classList.add('hidden');
    } else {
        if (checkbox) { checkbox.disabled = true; checkbox.checked = false; }
        if (label) {
            label.classList.add('opacity-50', 'cursor-not-allowed', 'text-gray-500');
            label.classList.remove('cursor-pointer', 'text-gray-300');
        }
        if (hint) hint.textContent = 'Complete Knock Frequency Calibration below to unlock.';
        const params = document.getElementById('knock-control-params');
        if (params) params.classList.add('hidden');
        if (calStatus) calStatus.classList.add('hidden');
        if (enterBtn) enterBtn.classList.remove('hidden');
    }
}

// --- Knock Spectrogram Chart ---
let spectrogramChartInstance = null;

function initSpectrogramChart() {
    const canvas = document.getElementById('knock-spectrogram-chart');
    if (!canvas || typeof Chart === 'undefined') return;
    spectrogramChartInstance = new Chart(canvas.getContext('2d'), {
        type: 'line',
        data: {
            labels: [],
            datasets: [
                { label: 'Noise',     borderColor: '#06B6D4', backgroundColor: 'transparent', data: [], tension: 0.3, pointRadius: 0, borderWidth: 1.5 },
                { label: 'Threshold', borderColor: '#EF4444', backgroundColor: 'transparent', data: [], tension: 0,   borderDash: [4, 4], pointRadius: 0, borderWidth: 1.5 }
            ]
        },
        options: {
            responsive: true, maintainAspectRatio: false,
            animation: { duration: 0 },
            scales: {
                x: { display: false },
                y: { min: -25, max: 0, grid: { color: '#374151' }, ticks: { color: '#9CA3AF', font: { size: 10 } } }
            },
            plugins: { legend: { display: false } }
        }
    });
}

function updateSpectrogramChart() {
    if (!spectrogramChartInstance || typeof spectrogramData === 'undefined') return;
    spectrogramChartInstance.data.labels = spectrogramData.map((_, i) => i);
    spectrogramChartInstance.data.datasets[0].data = spectrogramData.map(d => d.noise);
    spectrogramChartInstance.data.datasets[1].data = spectrogramData.map(d => d.threshold);
    spectrogramChartInstance.update('none');
}

// Make fixed-position adv-section tooltips follow the mouse cursor
document.addEventListener('mousemove', function(e) {
    document.querySelectorAll('.adv-section.adv-locked .adv-tooltip').forEach(tip => {
        tip.style.left = (e.clientX + 14) + 'px';
        tip.style.top  = (e.clientY - 28) + 'px';
    });
});
